import { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from 'react';
import { useAppData } from '../context/AppDataContext';
import {
  DndContext,
  closestCenter,
  pointerWithin,
  PointerSensor,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDroppable,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Blend,
  BlendRecipe,
  RoastBatch,
  OvenQueues,
  PackageTemplate,
  PackagingJob,
  PackagingInstructionInput,
  listBlends,
  createBlend,
  updateBlend,
  updateBatch,
  deleteBlend,
  reorderBlends,
  getDailyPlan,
  getBatch,
  createBatch,
  deleteBatch,
  cancelBatch,
  startBatch,
  startTimer,
  finishBatch,
  assignBatch,
  unassignBatch,
  reorderBatches,
  getOvenConfig,
  getVisionDeviceStatus,
  VisionDeviceStatus,
  listPackageTemplates,
  createPackageTemplate,
  updatePackageTemplate,
  deletePackageTemplate,
  createPackageRequirement,
  updatePackageRequirement,
  deletePackageRequirement,
  listPackagingJobs,
  createPackagingJob,
  updatePackagingJob,
  deletePackagingJob,
  completePackagingJob,
  reopenPackagingJob,
  reorderPackagingJobs,
  reorderCarryOverPackagingJobs,
} from '../api/client';
import { localDateString } from '../utils/date';
import BlendRecipeDisplay from '../components/BlendRecipeDisplay';
import PackagingQuantityCombobox from '../components/PackagingQuantityCombobox';
import { isValidPackagingQuantity } from '../utils/packagingQuantity';
import { ovenQueuePosition } from '../utils/ovenQueue';

function restoreFullscreen(wasFullscreen: boolean) {
  if (wasFullscreen && !document.fullscreenElement) {
    document.documentElement.requestFullscreen().catch(() => {
      // Some browsers may reject a fullscreen request after a native dialog.
    });
  }
}

function confirmWithoutLeavingFullscreen(message: string): boolean {
  const wasFullscreen = Boolean(document.fullscreenElement);
  const confirmed = window.confirm(message);
  restoreFullscreen(wasFullscreen);
  return confirmed;
}

function alertWithoutLeavingFullscreen(message: string): void {
  const wasFullscreen = Boolean(document.fullscreenElement);
  window.alert(message);
  restoreFullscreen(wasFullscreen);
}

const ROAST_LEVELS = [
  { label: 'Coffee 1',  color: '#748060' },
  { label: 'Coffee 2',  color: '#917D60' },
  { label: 'Coffee 3',  color: '#846A52' },
  { label: 'Coffee 4',  color: '#745642' },
  { label: 'Coffee 5',  color: '#5E4231' },
  { label: 'Coffee 6',  color: '#483227' },
  { label: 'Coffee 7',  color: '#332621' },
  { label: 'Coffee 8',  color: '#221D1B' },
  { label: 'Coffee 9',  color: '#1B1817' },
  { label: 'Coffee 10', color: '#141414' },
];

function hexToRgbTuple(hex: string): [number, number, number] {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return [r, g, b];
}

function scoreToRgb(score: number): [number, number, number] {
  const clamped = Math.max(0, Math.min(100, score));
  const t = (clamped / 100) * (ROAST_LEVELS.length - 1);
  const lo = Math.floor(t);
  const hi = Math.min(ROAST_LEVELS.length - 1, lo + 1);
  const f = t - lo;
  const [r0, g0, b0] = hexToRgbTuple(ROAST_LEVELS[lo].color);
  const [r1, g1, b1] = hexToRgbTuple(ROAST_LEVELS[hi].color);
  return [
    Math.round(r0 + (r1 - r0) * f),
    Math.round(g0 + (g1 - g0) * f),
    Math.round(b0 + (b1 - b0) * f),
  ];
}

function rgbToScore(rgb: [number, number, number]): number {
  let best = 0;
  let bestDist = Infinity;
  ROAST_LEVELS.forEach((level, i) => {
    const [r, g, b] = hexToRgbTuple(level.color);
    const dist = (rgb[0] - r) ** 2 + (rgb[1] - g) ** 2 + (rgb[2] - b) ** 2;
    if (dist < bestDist) { bestDist = dist; best = i; }
  });
  return Math.round((best / (ROAST_LEVELS.length - 1)) * 100);
}

function roastLevelLabel(score: number): { label: string; color: string } {
  const idx = Math.round((Math.max(0, Math.min(100, score)) / 100) * (ROAST_LEVELS.length - 1));
  return ROAST_LEVELS[idx];
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m} דק׳ ${s.toString().padStart(2, '0')} שנ׳`;
}

function formatClock(seconds: number): string {
  const clamped = Math.max(0, Math.round(seconds));
  const m = Math.floor(clamped / 60);
  const s = clamped % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

const MAX_ROAST_TIME_SECONDS = 86400;

function mutationErrorMessage(error: unknown): string {
  const detail = (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
  if (detail === 'Oven queue is full (two waiting batches)') return 'תור התנור מלא: כבר יש שתי אצוות שהועמסו וממתינות לכניסה לתנור.';
  return detail || 'השמירה נכשלה. בדקו את החיבור ונסו שוב.';
}

function mutationStatus(error: unknown): number | undefined {
  return (error as { response?: { status?: number } })?.response?.status;
}

// Live remaining seconds for a running timer, computed from the start timestamp.
function liveRemaining(batch: RoastBatch, nowMs: number): number | null {
  if (batch.timer_running && batch.timer_started_at && batch.timer_duration_seconds != null) {
    const startedMs = new Date(batch.timer_started_at).getTime();
    return Math.max(0, batch.timer_duration_seconds - (nowMs - startedMs) / 1000);
  }
  return batch.remaining_seconds ?? null;
}

function packagingQuantityLabel(item: { all_batch?: boolean; bag_count?: number | null }): string {
  return item.all_batch ? 'כל האצווה' : `${item.bag_count ?? 0} שקיות`;
}

function todayStr(): string {
  return localDateString();
}

const LOADING_WORKERS = ['אורן', 'אור', 'דניאל'];

function LoadingWorkerNamePicker({
  value,
  onChange,
  allowEmpty = true,
  disabled = false,
  compact = false,
  idPrefix,
  onEnter,
}: {
  value: string;
  onChange: (value: string) => void;
  allowEmpty?: boolean;
  disabled?: boolean;
  compact?: boolean;
  idPrefix: string;
  onEnter?: () => void;
}) {
  const initialMode = LOADING_WORKERS.includes(value) ? value : value ? 'custom' : '';
  const [mode, setMode] = useState(initialMode);
  const internalChangeRef = useRef(false);

  useEffect(() => {
    if (internalChangeRef.current) {
      internalChangeRef.current = false;
      return;
    }
    setMode(LOADING_WORKERS.includes(value) ? value : value ? 'custom' : '');
  }, [value]);

  const emitChange = (nextValue: string) => {
    if (nextValue === value) return;
    internalChangeRef.current = true;
    onChange(nextValue);
  };
  const choosePreset = (name: string) => {
    setMode(name);
    emitChange(name);
  };
  const chooseCustom = () => {
    setMode('custom');
    if (LOADING_WORKERS.includes(value)) emitChange('');
  };
  const chooseEmpty = () => {
    setMode('');
    emitChange('');
  };
  const buttonClass = (selected: boolean) => (
    `${compact ? 'px-2 py-1 text-xs' : 'px-4 py-3 text-base'} rounded-xl border-2 font-bold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
      selected
        ? 'border-amber-600 bg-amber-100 text-amber-900'
        : 'border-gray-200 bg-white text-gray-700 hover:border-amber-300'
    }`
  );

  return (
    <div dir="rtl">
      <div className={`grid gap-2 ${allowEmpty ? 'grid-cols-5' : 'grid-cols-4'}`}>
        {LOADING_WORKERS.map((name) => (
          <button
            key={name}
            type="button"
            disabled={disabled}
            onClick={() => choosePreset(name)}
            aria-pressed={mode === name}
            className={buttonClass(mode === name)}
          >
            {name}
          </button>
        ))}
        <button
          type="button"
          disabled={disabled}
          onClick={chooseCustom}
          aria-pressed={mode === 'custom'}
          className={buttonClass(mode === 'custom')}
        >
          אחר
        </button>
        {allowEmpty && (
          <button
            type="button"
            disabled={disabled}
            onClick={chooseEmpty}
            aria-pressed={mode === ''}
            className={buttonClass(mode === '')}
          >
            ללא
          </button>
        )}
      </div>
      {mode === 'custom' && (
        <input
          id={`${idPrefix}-custom`}
          type="text"
          maxLength={100}
          disabled={disabled}
          value={LOADING_WORKERS.includes(value) ? '' : value}
          onChange={event => emitChange(event.target.value)}
          onKeyDown={event => {
            if (event.key === 'Enter' && onEnter) {
              event.preventDefault();
              onEnter();
            }
          }}
          placeholder="הקלד/י שם"
          autoFocus
          className={`mt-2 w-full rounded-xl border-2 border-gray-200 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-200 disabled:bg-gray-100 ${
            compact ? 'px-2 py-1 text-sm' : 'px-4 py-3 text-lg'
          }`}
        />
      )}
    </div>
  );
}

// ── Blend Card (HTML5 native drag for blend→plan) ────────────────────

function BlendCard({ blend, onClick }: { blend: Blend; onClick: () => void }) {
  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData('application/blend-id', blend.id);
    e.dataTransfer.effectAllowed = 'copy';
  };

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      onClick={onClick}
      className="bg-amber-50 hover:bg-amber-100 active:bg-amber-200 border-2 border-amber-200 hover:border-amber-400 rounded-xl p-3 pt-8 h-full text-center transition-all touch-manipulation cursor-grab active:cursor-grabbing select-none"
    >
      <div className="flex items-center justify-center gap-2 mb-1">
        <div className="font-bold text-sm text-gray-800 break-words line-clamp-2 min-w-0" title={blend.name}>{blend.name}</div>
        {blend.target_color_rgb && (
          <div
            className="w-3 h-3 rounded-full border border-gray-300 shrink-0"
            style={{ backgroundColor: `rgb(${blend.target_color_rgb.join(',')})` }}
            title={`צבע יעד: rgb(${blend.target_color_rgb.join(', ')})`}
          />
        )}
      </div>
      {blend.target_roast_score != null && (
        <div className="text-xs text-amber-700 font-semibold mt-0.5">יעד: {Math.round(blend.target_roast_score)}/100</div>
      )}
    </div>
  );
}

function SortableBlend({ blend, disabled, children }: {
  blend: Blend;
  disabled: boolean;
  children: React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: blend.id, disabled,
  });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`relative min-w-0 ${isDragging ? 'z-10 opacity-70 shadow-lg' : ''}`}
    >
      <button
        type="button"
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        disabled={disabled}
        aria-label={`שינוי סדר ${blend.name}`}
        title="גרור לשינוי סדר; במקלדת לחץ רווח והשתמש בחצים"
        onClick={event => event.stopPropagation()}
        className="absolute top-0 right-1 z-10 px-2 py-1 text-gray-500 hover:text-amber-800 rounded-lg focus-visible:ring-2 focus-visible:ring-amber-600 cursor-grab active:cursor-grabbing touch-none disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <span aria-hidden="true">⠿</span>
      </button>
      {children}
    </div>
  );
}

function SortableBlendGrid({ blends, className, onDragStart, onDragEnd, onDragCancel, children }: {
  blends: Blend[];
  className: string;
  onDragStart: () => void;
  onDragEnd: (event: DragEndEvent) => void;
  onDragCancel: () => void;
  children: (blend: Blend) => React.ReactNode;
}) {
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={args => args.pointerCoordinates ? pointerWithin(args) : closestCenter(args)}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
      accessibility={{
        screenReaderInstructions: {
          draggable: 'לשינוי סדר לחץ רווח, הזז בעזרת החצים, לחץ רווח לשמירה או Escape לביטול.',
        },
        announcements: {
          onDragStart: ({ active }) => `שינוי סדר ${blends.find(blend => blend.id === active.id)?.name ?? ''}`,
          onDragOver: ({ over }) => over ? `מיקום ${blends.findIndex(blend => blend.id === over.id) + 1} מתוך ${blends.length}` : 'מחוץ לרשימה',
          onDragEnd: ({ active, over }) => over && active.id !== over.id ? 'הסדר נשלח לשמירה' : 'שינוי הסדר בוטל',
          onDragCancel: () => 'שינוי הסדר בוטל',
        },
      }}
    >
      <SortableContext items={blends.map(blend => blend.id)} strategy={rectSortingStrategy}>
        <div className={className}>
          {blends.map(blend => (
            <SortableBlend key={blend.id} blend={blend} disabled={blends.length > 100}>
              {children(blend)}
            </SortableBlend>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

// ── Sortable batch item (dnd-kit for reordering) ─────────────────────

function PackageSummary({ batch, onOpen, inverted = false }: { batch: RoastBatch; onOpen: (batch: RoastBatch) => void; inverted?: boolean }) {
  const requirements = batch.package_requirements ?? [];
  return (
    <button
      type="button"
      onClick={(event) => { event.stopPropagation(); onOpen(batch); }}
      className={`mt-1 max-w-full text-right text-xs font-semibold rounded-lg px-2 py-1 ${inverted ? 'bg-white/20 text-white' : 'bg-purple-50 text-purple-700 hover:bg-purple-100'}`}
      title={requirements.map(item => `${item.customer_name}: ${packagingQuantityLabel(item)} — ${item.comment}`).join('\n') || 'הוסף הוראות אריזה'}
    >
      {requirements.length ? (
        <div className="flex flex-col items-end gap-0.5">
          {requirements.map((item, i) => (
            <div key={i}>
              {i === 0 ? '🛍 ' : ''}{item.customer_name} · {packagingQuantityLabel(item)}{item.comment ? ` — ${item.comment}` : ''}
            </div>
          ))}
        </div>
      ) : (
        <>🛍 הוסף הוראות אריזה</>
      )}
    </button>
  );
}

function PackageInstructionIcon({ batch }: { batch: RoastBatch }) {
  const requirements = batch.package_requirements ?? [];
  if (requirements.length === 0) return null;

  const label = requirements.length === 1
    ? 'יש הוראת אריזה'
    : `יש ${requirements.length} הוראות אריזה`;

  return (
    <span
      className="shrink-0 text-sm"
      role="img"
      aria-label={label}
      title={label}
    >
      🛍
    </span>
  );
}

function CarryOverDate({ batch, currentPlanDate }: { batch: RoastBatch; currentPlanDate: string }) {
  if (batch.plan_date === currentPlanDate) return null;
  return <div className="text-xs font-semibold text-orange-700">הועבר מתאריך {batch.plan_date}</div>;
}

function applyPlannedBatchOrder(batches: RoastBatch[], orderedIds: string[]): RoastBatch[] {
  const order = new Map(orderedIds.map((id, index) => [id, index]));
  const reordered = batches
    .filter(batch => order.has(batch.id))
    .sort((a, b) => order.get(a.id)! - order.get(b.id)!)
    .map((batch, index) => ({ ...batch, sort_order: index }));
  const reorderedIds = new Set(orderedIds);
  return [...reordered, ...batches.filter(batch => !reorderedIds.has(batch.id))];
}

function applyPackagingOrder(
  jobs: PackagingJob[],
  status: 'pending' | 'done',
  orderedIds: string[],
): PackagingJob[] {
  const order = new Map(orderedIds.map((id, index) => [id, index]));
  const reordered = jobs
    .filter(job => job.status === status && order.has(job.id))
    .sort((a, b) => order.get(a.id)! - order.get(b.id)!)
    .map((job, index) => ({ ...job, sort_order: index }));
  let replacementIndex = 0;
  return jobs.map(job =>
    job.status === status && order.has(job.id)
      ? reordered[replacementIndex++]!
      : job,
  );
}

function LoaderNameEditor({
  batch,
  onChange,
}: {
  batch: RoastBatch;
  onChange: (batch: RoastBatch, loadingWorkerName: string | null) => Promise<boolean>;
}) {
  const [loaderName, setLoaderName] = useState(batch.loading_worker_name ?? '');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setLoaderName(batch.loading_worker_name ?? '');
  }, [batch.loading_worker_name]);

  const normalizedName = loaderName.trim() || null;
  const changed = normalizedName !== (batch.loading_worker_name ?? null);
  const save = async () => {
    if (!changed || saving) return;
    setSaving(true);
    const saved = await onChange(batch, normalizedName);
    if (!saved) setLoaderName(batch.loading_worker_name ?? '');
    setSaving(false);
  };

  return (
    <div className="mt-2" dir="rtl">
      <div className="mb-1 text-xs font-bold text-gray-600">שם המעמיס</div>
      <LoadingWorkerNamePicker
        idPrefix={`loader-name-${batch.id}`}
        value={loaderName}
        onChange={setLoaderName}
        disabled={saving}
        compact
        onEnter={() => void save()}
      />
      <div className="mt-2 flex justify-end">
        <button
          type="button"
          onClick={() => void save()}
          disabled={!changed || saving}
          className="shrink-0 rounded-lg bg-teal-700 px-3 py-1 text-xs font-bold text-white hover:bg-teal-800 disabled:bg-gray-300"
        >
          {saving ? 'שומר…' : 'שמור'}
        </button>
      </div>
    </div>
  );
}

function RoastTimeEditor({
  batch,
  onChange,
}: {
  batch: RoastBatch;
  onChange: (batch: RoastBatch, roastTimeSeconds: number) => Promise<boolean>;
}) {
  const initialSeconds = batch.planned_roast_time_seconds ?? 600;
  const [minutes, setMinutes] = useState(Math.floor(initialSeconds / 60));
  const [seconds, setSeconds] = useState(initialSeconds % 60);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const duration = batch.planned_roast_time_seconds ?? 600;
    setMinutes(Math.floor(duration / 60));
    setSeconds(duration % 60);
  }, [batch.planned_roast_time_seconds]);

  const totalSeconds = minutes * 60 + seconds;
  const changed = totalSeconds !== batch.planned_roast_time_seconds;
  const valid = Number.isInteger(minutes)
    && Number.isInteger(seconds)
    && minutes >= 0
    && seconds >= 0
    && seconds <= 59
    && totalSeconds >= 1
    && totalSeconds <= MAX_ROAST_TIME_SECONDS;

  const save = async () => {
    if (!changed || !valid || saving) return;
    setSaving(true);
    const saved = await onChange(batch, totalSeconds);
    if (!saved) {
      const duration = batch.planned_roast_time_seconds ?? 600;
      setMinutes(Math.floor(duration / 60));
      setSeconds(duration % 60);
    }
    setSaving(false);
  };

  return (
    <div className="mt-2" dir="rtl">
      <div className="mb-1 text-xs font-bold text-gray-600">זמן קלייה מתוכנן</div>
      <div className="flex items-center gap-2">
        <input
          type="number"
          min={0}
          max={1440}
          value={minutes}
          onChange={event => setMinutes(Number(event.target.value))}
          disabled={saving}
          aria-label="דקות קלייה"
          className="w-20 rounded-lg border-2 border-gray-300 px-2 py-1 text-center font-bold"
        />
        <span className="text-xs font-bold text-gray-500">דקות</span>
        <input
          type="number"
          min={0}
          max={59}
          value={seconds}
          onChange={event => setSeconds(Number(event.target.value))}
          disabled={saving}
          aria-label="שניות קלייה"
          className="w-20 rounded-lg border-2 border-gray-300 px-2 py-1 text-center font-bold"
        />
        <span className="text-xs font-bold text-gray-500">שניות</span>
        <button
          type="button"
          onClick={() => void save()}
          disabled={!changed || !valid || saving}
          className="mr-auto shrink-0 rounded-lg bg-amber-700 px-3 py-1 text-xs font-bold text-white hover:bg-amber-800 disabled:bg-gray-300"
        >
          {saving ? 'שומר…' : 'שמור'}
        </button>
      </div>
      {!valid && <div role="alert" className="mt-1 text-xs font-bold text-red-600">יש להזין זמן בין שנייה אחת ל־24 שעות.</div>}
    </div>
  );
}

function SortableBatchItem({
  batch,
  currentPlanDate,
  isExpanded,
  spacious = false,
  onToggle,
  onDelete,
  onAssign,
  onPackage,
  onLoaderNameChange,
  onRoastTimeChange,
  onMove,
  canMoveUp,
  canMoveDown,
  ovenQueues,
  canStart,
  onStart,
  onUnassign,
}: {
  batch: RoastBatch;
  currentPlanDate: string;
  isExpanded: boolean;
  spacious?: boolean;
  onToggle: () => void;
  onDelete: (id: string) => void;
  onAssign: (id: string, mid: string) => void;
  onPackage: (batch: RoastBatch) => void;
  onLoaderNameChange: (batch: RoastBatch, loadingWorkerName: string | null) => Promise<boolean>;
  onRoastTimeChange: (batch: RoastBatch, roastTimeSeconds: number) => Promise<boolean>;
  onMove: (id: string, direction: -1 | 1) => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
  ovenQueues: OvenQueues;
  canStart: boolean;
  onStart: (id: string, mid: string) => void;
  onUnassign: (id: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: batch.id });
  const detailsId = `planned-batch-details-${batch.id}`;
  const queuePosition = ovenQueuePosition(ovenQueues, batch);
  const cardNodeRef = useRef<HTMLDivElement | null>(null);
  const previousTopRef = useRef<number | null>(null);
  useLayoutEffect(() => {
    const node = cardNodeRef.current;
    if (!node) return;
    const nextTop = node.getBoundingClientRect().top;
    if (previousTopRef.current !== null && previousTopRef.current !== nextTop && !isDragging) {
      node.animate(
        [{ transform: `translateY(${previousTopRef.current - nextTop}px)` }, { transform: 'translateY(0)' }],
        { duration: 220, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)' },
      );
    }
    previousTopRef.current = nextTop;
  }, [batch.sort_order, isDragging]);
  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition ?? 'transform 220ms cubic-bezier(0.2, 0.8, 0.2, 1)',
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={node => {
        cardNodeRef.current = node;
        setNodeRef(node);
      }}
      style={style}
      className={`bg-white border-2 border-gray-200 rounded-xl shadow-sm transition-[padding] ${spacious ? 'px-4 py-3' : 'px-2 py-1.5'}`}
    >
      <div className="flex items-center gap-1.5 min-w-0">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={isExpanded}
          aria-controls={detailsId}
          className={`flex min-w-0 flex-1 items-center rounded-lg text-right hover:bg-gray-50 ${spacious ? 'gap-3 px-2 py-2' : 'gap-1.5 px-1 py-1'}`}
        >
          <span className="text-xs text-gray-500 shrink-0" aria-hidden="true">{isExpanded ? '▼' : '◀'}</span>
          <span className={`font-bold text-gray-800 truncate ${spacious ? 'text-xl' : 'text-base'}`} dir="auto" title={batch.blend_name}>{batch.blend_name}</span>
          <PackageInstructionIcon batch={batch} />
        </button>
        <div className="flex items-center gap-0.5 shrink-0">
          <button
            type="button"
            onClick={() => onMove(batch.id, -1)}
            disabled={!canMoveUp}
            className={`${spacious ? 'w-10 h-10 text-lg' : 'w-7 h-8 text-sm'} bg-gray-100 text-gray-700 rounded-lg font-bold hover:bg-gray-200 disabled:opacity-25 disabled:hover:bg-gray-100`}
            title="העבר למעלה"
            aria-label="העבר אצווה למעלה"
          >
            ↑
          </button>
          <button
            type="button"
            onClick={() => onMove(batch.id, 1)}
            disabled={!canMoveDown}
            className={`${spacious ? 'w-10 h-10 text-lg' : 'w-7 h-8 text-sm'} bg-gray-100 text-gray-700 rounded-lg font-bold hover:bg-gray-200 disabled:opacity-25 disabled:hover:bg-gray-100`}
            title="העבר למטה"
            aria-label="העבר אצווה למטה"
          >
            ↓
          </button>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {OVEN_IDS.map((mid) => {
            const selected = batch.machine_id === mid;
            const positionLabel = selected && queuePosition > 0
              ? ` — ${queuePosition === 1 ? 'ראשונה' : 'שנייה'} בתור`
              : '';
            const label = selected ? `הועמס לתנור ${mid}${positionLabel}` : `שייך לתנור ${mid}`;
            return (
              <button
                key={mid}
                type="button"
                onClick={() => onAssign(batch.id, mid)}
                disabled={!!batch.machine_id}
                aria-pressed={selected}
                aria-label={label}
                className={`${spacious ? 'h-10 px-4 text-base' : 'h-8 px-2 text-xs'} ${
                  selected && queuePosition === 1 ? 'bg-green-600 text-white'
                    : selected && queuePosition === 2 ? 'bg-orange-500 text-white'
                    : 'bg-amber-100 text-amber-800 enabled:hover:bg-amber-200'
                } rounded-lg font-bold transition-colors whitespace-nowrap disabled:cursor-default`}
                title={label}
              >
                ← {mid}
              </button>
            );
          })}
        </div>
      </div>
      <LoadingWorker batch={batch} />

      <div id={detailsId} hidden={!isExpanded} className={spacious ? 'mt-3 border-t border-gray-100 pt-3' : 'mt-1.5 border-t border-gray-100 pt-1.5'}>
        <div className="flex items-center gap-2 min-w-0">
          <button
            {...attributes}
            {...listeners}
            className="cursor-grab text-gray-400 hover:text-gray-600 text-xl p-1 shrink-0"
            title="גרור לשינוי סדר"
            aria-label="גרור לשינוי סדר"
          >
            ⠿
          </button>
          <div className={`min-w-0 flex-1 text-gray-500 whitespace-nowrap ${spacious ? 'text-base' : 'text-xs'}`} dir="rtl">
            <bdi>{batch.quantity}</bdi> {batch.unit === 'kg' ? 'ק״ג' : 'batchs'}
            {batch.batch_weight_kg != null && <span className="mr-2 font-bold text-amber-700">· <bdi>{batch.batch_weight_kg}</bdi> ק״ג לאצווה</span>}
          </div>
          {!batch.machine_id && <button
            type="button"
            onClick={() => onDelete(batch.id)}
            className="text-red-400 hover:text-red-600 text-xl p-1 shrink-0"
            title="מחק"
            aria-label="מחק אצווה"
          >
            ✕
          </button>}
        </div>
        <CarryOverDate batch={batch} currentPlanDate={currentPlanDate} />
        <LoaderNameEditor batch={batch} onChange={onLoaderNameChange} />
        <RoastTimeEditor batch={batch} onChange={onRoastTimeChange} />
        <PackageSummary batch={batch} onOpen={onPackage} />
        {batch.machine_id && (
          <div className="mt-2 flex items-center gap-2">
            <span className="text-xs font-bold text-gray-600">{queuePosition === 1 ? 'ראשונה בתור' : queuePosition === 2 ? 'שנייה בתור' : 'ממתין לרענון התור'}</span>
            <button type="button" disabled={!canStart} onClick={() => { if (batch.machine_id) onStart(batch.id, batch.machine_id); }}
              className="rounded-lg bg-green-600 px-3 py-1.5 text-xs font-bold text-white enabled:hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-gray-300">
              הכנס לתנור
            </button>
            <button type="button" onClick={() => onUnassign(batch.id)} className="rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-bold text-gray-700 hover:bg-gray-200">
              בטל שיוך לתנור
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Blend Selector Banner (for in-list unknown blend replacement) ─────

function BlendSelectorBanner({ batch, onBlendSet }: { batch: RoastBatch; onBlendSet: (b: RoastBatch) => void }) {
  const [allBlends, setAllBlends] = useState<{ id: string; name: string }[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    listBlends().then(data => setAllBlends(data.map(b => ({ id: b.id, name: b.name })))).catch(() => {});
  }, []);

  const handleSave = async () => {
    if (!selectedId) return;
    const blend = allBlends.find(b => b.id === selectedId);
    if (!blend) return;
    setSaving(true);
    setError('');
    try {
      const updated = await updateBatch(batch.id, { blend_id: blend.id, blend_name: blend.name });
      onBlendSet(updated);
    } catch (err) {
      console.error('Failed to set blend:', err);
      setError(mutationErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap mt-1">
      <select
        value={selectedId}
        onChange={e => setSelectedId(e.target.value)}
        className="flex-1 px-2 py-1 border border-orange-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-400 outline-none bg-white min-w-0"
      >
        <option value="">-- בחר תערובת --</option>
        {allBlends.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
      </select>
      <button
        onClick={handleSave}
        disabled={!selectedId || saving}
        className="px-3 py-1 bg-orange-600 text-white rounded-lg text-sm font-bold hover:bg-orange-700 disabled:opacity-50 shrink-0"
      >
        {saving ? '...' : 'שמור'}
      </button>
      {error && <div role="alert" className="w-full text-sm font-bold text-red-700">{error}</div>}
    </div>
  );
}

// ── Oven identifiers (one panel per oven) ────────────────────────────
const OVEN_IDS = ['1', '2'];
const OVEN_2_VISIBILITY_KEY = 'roaster.manager.showOven2';

function initialOven2Visibility(): boolean {
  try {
    return localStorage.getItem(OVEN_2_VISIBILITY_KEY) !== 'false';
  } catch {
    return true;
  }
}

// ── Completed batch item (for done batches in the left panel) ────────

function isUnknown(batch: RoastBatch): boolean {
  return batch.blend_id === 'unknown' || batch.blend_name === 'Unknown';
}

function LoadingWorker({ batch, inverted = false }: { batch: RoastBatch; inverted?: boolean }) {
  if (!batch.loading_worker_name) return null;
  return (
    <div className={`mt-1 text-xs font-bold ${inverted ? 'text-white' : 'text-teal-700'}`}>
      👤 העמסה: {batch.loading_worker_name}
    </div>
  );
}

function CompletedBatchItem({
  batch,
  isExpanded,
  onToggle,
  onBatchUpdated,
  onPackage,
}: {
  batch: RoastBatch;
  isExpanded: boolean;
  onToggle: () => void;
  onBatchUpdated?: (b: RoastBatch) => void;
  onPackage: (b: RoastBatch) => void;
}) {
  const unknown = isUnknown(batch);
  const detailsId = `completed-batch-details-${batch.id}`;

  return (
    <div className={`rounded-xl px-2 py-1.5 border ${unknown ? 'bg-orange-50 border-orange-300' : 'bg-green-50 border-green-200'}`}>
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={isExpanded}
          aria-controls={detailsId}
          className="flex min-w-0 flex-1 items-center gap-1.5 rounded-lg px-1 py-1 text-right hover:bg-black/5"
        >
          <span className="text-xs text-gray-500 shrink-0" aria-hidden="true">{isExpanded ? '▼' : '◀'}</span>
          <span className={`text-base shrink-0 ${unknown ? 'text-orange-500' : 'text-green-500'}`}>{unknown ? '⚠️' : '✓'}</span>
          <span className={`font-bold text-base truncate ${unknown ? 'text-orange-700' : 'text-gray-700'}`} dir="auto" title={batch.blend_name}>
            {batch.blend_name}
          </span>
          <PackageInstructionIcon batch={batch} />
        </button>
        <span className={`shrink-0 px-2 py-1 text-xs rounded-full font-bold ${unknown ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}`}>
          {unknown ? 'לא ידוע' : 'הושלם'}
        </span>
      </div>
      <LoadingWorker batch={batch} />
      <div id={detailsId} hidden={!isExpanded} className="mt-1.5 border-t border-current/10 pt-1.5">
        <div className="text-xs text-gray-500" dir="rtl">
          <bdi>{batch.quantity}</bdi> {batch.unit === 'kg' ? 'ק״ג' : 'batchs'}
          {batch.batch_weight_kg != null && <span className="mr-2 font-bold text-amber-700">· <bdi>{batch.batch_weight_kg}</bdi> ק״ג לאצווה</span>}
        </div>
        <PackageSummary batch={batch} onOpen={onPackage} />
        {unknown && onBatchUpdated && (
          <BlendSelectorBanner batch={batch} onBlendSet={onBatchUpdated} />
        )}
      </div>
    </div>
  );
}

// ── Oven panel (droppable: drag a planned batch here to assign it) ────

// Assigned to an oven but not yet roasting (planned + machine_id): Start / Remove.
function AssignedBatchItem({
  batch,
  currentPlanDate,
  isExpanded,
  ovenOccupied,
  onToggle,
  onStart,
  onUnassign,
  onPackage,
  onLoaderNameChange,
  onRoastTimeChange,
}: {
  batch: RoastBatch;
  currentPlanDate: string;
  isExpanded: boolean;
  ovenOccupied: boolean;
  onToggle: () => void;
  onStart: (id: string) => void;
  onUnassign: (id: string) => void;
  onPackage: (batch: RoastBatch) => void;
  onLoaderNameChange: (batch: RoastBatch, loadingWorkerName: string | null) => Promise<boolean>;
  onRoastTimeChange: (batch: RoastBatch, roastTimeSeconds: number) => Promise<boolean>;
}) {
  const detailsId = `assigned-batch-details-${batch.id}`;

  return (
    <div className="bg-blue-50 border-2 border-blue-200 rounded-xl px-2 py-1.5 shadow-sm">
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={isExpanded}
          aria-controls={detailsId}
          className="flex min-w-0 flex-1 items-center gap-1.5 rounded-lg px-1 py-1 text-right hover:bg-blue-100"
        >
          <span className="text-xs text-gray-500 shrink-0" aria-hidden="true">{isExpanded ? '▼' : '◀'}</span>
          <span className="font-bold text-base text-gray-800 truncate" dir="auto" title={batch.blend_name}>{batch.blend_name}</span>
          <PackageInstructionIcon batch={batch} />
        </button>
        <span className="shrink-0 px-2 py-1 text-xs rounded-full font-bold bg-blue-100 text-blue-700">
          ממתין לכניסה לתנור
        </span>
      </div>
      <LoadingWorker batch={batch} />
      <div id={detailsId} hidden={!isExpanded} className="mt-1.5 border-t border-blue-200 pt-1.5">
        <div className="text-xs text-gray-500" dir="rtl">
          <bdi>{batch.quantity}</bdi> {batch.unit === 'kg' ? 'ק״ג' : 'batchs'}
          {batch.batch_weight_kg != null && <span className="mr-2 font-bold text-amber-700">· <bdi>{batch.batch_weight_kg}</bdi> ק״ג לאצווה</span>}
        </div>
        <CarryOverDate batch={batch} currentPlanDate={currentPlanDate} />
        <LoaderNameEditor batch={batch} onChange={onLoaderNameChange} />
        <RoastTimeEditor batch={batch} onChange={onRoastTimeChange} />
        <PackageSummary batch={batch} onOpen={onPackage} />
        <div className="mt-1.5 flex items-center gap-2">
          <button
            type="button"
            onClick={() => onStart(batch.id)}
            disabled={ovenOccupied}
            className="px-2 py-1.5 bg-green-600 text-white rounded-lg text-xs font-bold hover:bg-green-700 transition-colors disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            ▶ התחל
          </button>
          <button
            type="button"
            onClick={() => onUnassign(batch.id)}
            className="text-gray-400 hover:text-gray-600 text-xl p-2"
            title="הסר מהתנור"
            aria-label="הסר מהתנור"
          >
            ↩
          </button>
        </div>
      </div>
    </div>
  );
}

// In-progress roast: live timer + Start timer / Finish / Cancel.
function RoastingBatchItem({
  batch,
  currentPlanDate,
  onStartTimer,
  onFinish,
  onCancel,
  onBatchUpdated,
  nowMs,
  onPackage,
}: {
  batch: RoastBatch;
  currentPlanDate: string;
  onStartTimer: (id: string) => void;
  onFinish: (id: string) => void;
  onCancel: (id: string) => void;
  onBatchUpdated: (batch: RoastBatch) => void;
  nowMs: number;
  onPackage: (batch: RoastBatch) => void;
}) {
  const remaining = liveRemaining(batch, nowMs);
  const isDone = batch.timer_running && remaining !== null && remaining <= 0;
  const nearDone = batch.timer_running && !isDone && remaining !== null && remaining <= 60;

  const cardClass = isDone
    ? 'bg-red-600 border-red-500 animate-pulse'
    : nearDone
    ? 'bg-orange-50 border-orange-400'
    : 'bg-yellow-50 border-yellow-300';

  return (
    <div dir="rtl" className={`flex flex-col gap-1.5 border-2 rounded-xl p-2 shadow-sm transition-all duration-500 ${cardClass}`}>
      <div className="flex items-start gap-2 min-w-0">
        <div className={`text-base shrink-0 ${isDone ? 'animate-bounce' : ''}`}>{isDone ? '⏰' : '🔥'}</div>
        <div className="min-w-0 flex-1 text-right">
          <div className="flex items-center gap-1 min-w-0">
            <div className={`min-w-0 flex-1 font-bold text-base truncate text-right ${isDone ? 'text-white' : 'text-gray-800'}`} dir="auto" title={batch.blend_name}>
              {batch.blend_name}
            </div>
            <PackageInstructionIcon batch={batch} />
          </div>
          <CarryOverDate batch={batch} currentPlanDate={currentPlanDate} />
          <div className={`flex items-center justify-between gap-2 text-xs whitespace-nowrap ${isDone ? 'text-red-100' : 'text-gray-500'}`}>
            <span><bdi>{batch.quantity}</bdi> {batch.unit === 'kg' ? 'ק״ג' : 'batchs'}</span>
            {batch.batch_weight_kg != null && <span className={isDone ? 'text-red-100' : 'font-bold text-amber-700'}>· <bdi>{batch.batch_weight_kg}</bdi> ק״ג לאצווה</span>}
            {batch.timer_running && remaining != null && !isDone && (
              <span className={`font-bold tabular-nums ${nearDone ? 'text-orange-600' : 'text-amber-700'}`}>
                ⏱ {formatClock(remaining)}
              </span>
            )}
            {isDone && (
              <span className="font-black text-yellow-200">⏱ 0:00 — סיים!</span>
            )}
          </div>
        </div>
      </div>
      <PackageSummary batch={batch} onOpen={onPackage} inverted={isDone} />
      <LoadingWorker batch={batch} inverted={isDone} />
      {isUnknown(batch) && (
        <BlendSelectorBanner batch={batch} onBlendSet={onBatchUpdated} />
      )}
      <div className="flex items-stretch gap-1 pt-2 border-t border-current/10">
        {!batch.timer_running && (
          <button
            onClick={() => onStartTimer(batch.id)}
            className="flex-1 min-w-0 px-1 py-1.5 bg-amber-600 text-white rounded-lg text-xs font-bold hover:bg-amber-700 transition-colors whitespace-nowrap"
          >
            ▶ טיימר
          </button>
        )}
        <button
          onClick={() => onFinish(batch.id)}
          className={`flex-1 min-w-0 px-1 py-1.5 rounded-lg text-xs font-bold transition-colors whitespace-nowrap ${
            isDone
              ? 'bg-yellow-300 text-red-900 hover:bg-yellow-200 animate-bounce'
              : 'bg-green-600 text-white hover:bg-green-700'
          }`}
        >
          ■ סיים
        </button>
        <button
          onClick={() => onCancel(batch.id)}
          className={`w-9 shrink-0 rounded-lg text-lg ${isDone ? 'text-red-200 hover:text-white' : 'text-red-400 hover:text-red-600'}`}
          title="ביטול"
        >
          ✕
        </button>
      </div>
    </div>
  );
}

// ── Stray oven occupant: a batch left assigned to a machine from a past
// plan date. It silently blocks new assignments to that oven without ever
// appearing on today's plan, so it's surfaced here with the same finish /
// cancel / remove actions available on a normal in-oven batch. ──
function StrayOccupantBanner({
  batches,
  onFinish,
  onCancel,
  onUnassign,
}: {
  batches: RoastBatch[];
  onFinish: (id: string) => void;
  onCancel: (id: string) => void;
  onUnassign: (id: string) => void;
}) {
  if (batches.length === 0) return null;
  return (
    <div dir="rtl" role="alert" className="bg-red-50 border-2 border-red-300 rounded-xl px-5 py-4 shadow-sm shrink-0">
      <div className="font-bold text-xl text-red-700 mb-3">
        ⚠ נמצאו אצוות ישנות שעדיין תופסות תנור ({batches.length})
      </div>
      <div className="space-y-2">
        {batches.map((batch) => (
          <div key={batch.id} className="flex items-center justify-between gap-2 bg-white border border-red-200 rounded-xl p-3">
            <div className="min-w-0 flex-1">
              <div className="font-bold text-base text-gray-800 break-words" dir="auto" title={batch.blend_name}>
                {batch.blend_name} — תנור {batch.machine_id}
              </div>
              <div className="text-sm text-gray-500">
                מתאריך {batch.plan_date} · סטטוס: {batch.status === 'in-progress' ? 'בביצוע' : 'ממתין'}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {batch.status === 'in-progress' ? (
                <>
                  <button
                    onClick={() => onFinish(batch.id)}
                    className="px-3 py-2 bg-green-600 text-white rounded-xl text-sm font-bold hover:bg-green-700 transition-colors"
                  >
                    ■ סיים
                  </button>
                  <button
                    onClick={() => onCancel(batch.id)}
                    className="px-3 py-2 bg-red-100 text-red-700 rounded-xl text-sm font-bold hover:bg-red-200 transition-colors"
                  >
                    ✕ בטל
                  </button>
                </>
              ) : (
                <button
                  onClick={() => onUnassign(batch.id)}
                  className="px-3 py-2 bg-red-100 text-red-700 rounded-xl text-sm font-bold hover:bg-red-200 transition-colors"
                >
                  ↩ הסר מהתנור
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function OvenPanel({
  mid,
  items,
  hasExternalActive,
  currentPlanDate,
  expandedBatchId,
  onToggleBatch,
  onStart,
  onUnassign,
  onStartTimer,
  onFinish,
  onCancel,
  onBatchUpdated,
  onPackage,
  onLoaderNameChange,
  onRoastTimeChange,
  ovenQueues,
}: {
  mid: string;
  items: RoastBatch[];
  hasExternalActive: boolean;
  currentPlanDate: string;
  expandedBatchId: string | null;
  onToggleBatch: (id: string) => void;
  onStart: (id: string, mid: string) => void;
  onUnassign: (id: string) => void;
  onStartTimer: (id: string) => void;
  onFinish: (id: string) => void;
  onCancel: (id: string) => void;
  onBatchUpdated: (b: RoastBatch) => void;
  onPackage: (batch: RoastBatch) => void;
  onLoaderNameChange: (batch: RoastBatch, loadingWorkerName: string | null) => Promise<boolean>;
  onRoastTimeChange: (batch: RoastBatch, roastTimeSeconds: number) => Promise<boolean>;
  ovenQueues: OvenQueues;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `oven-${mid}` });
  const [nowMs, setNowMs] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const running = items.filter((b) => b.status === 'in-progress');
  const assigned = items.filter((b) => b.status === 'planned')
    .sort((a, b) => ovenQueuePosition(ovenQueues, a) - ovenQueuePosition(ovenQueues, b));
  const done = items.filter((b) => b.status === 'done').sort((a, b) => {
    const aEnd = Date.parse(a.end_time ?? '');
    const bEnd = Date.parse(b.end_time ?? '');
    if (!Number.isFinite(aEnd)) return Number.isFinite(bEnd) ? 1 : 0;
    if (!Number.isFinite(bEnd)) return -1;
    return bEnd - aEnd;
  });

  return (
    <div
      ref={setNodeRef}
      className={`bg-white rounded-2xl shadow-lg p-4 2xl:p-6 flex flex-col overflow-hidden transition-all ${
        isOver ? 'ring-4 ring-amber-400 bg-amber-50' : ''
      }`}
    >
      <h2 className="text-base 2xl:text-lg font-bold text-gray-700 mb-2 shrink-0">תנור {mid}</h2>
      {items.length === 0 ? (
        <p className="text-gray-400 text-lg text-center py-6">גרור אצווה מהתכנית כדי לשייך</p>
      ) : (
        <div className="space-y-1.5 overflow-y-auto flex-1 min-h-0">
          {/* Roasting (pinned at top) */}
          {running.map((batch) => (
            <RoastingBatchItem
              key={batch.id}
              batch={batch}
              currentPlanDate={currentPlanDate}
              onStartTimer={onStartTimer}
              onFinish={onFinish}
              onCancel={onCancel}
              onBatchUpdated={onBatchUpdated}
              nowMs={nowMs}
              onPackage={onPackage}
            />
          ))}
          {/* Assigned, awaiting start */}
          {assigned.map((batch) => (
            <AssignedBatchItem
              key={batch.id}
              batch={batch}
              currentPlanDate={currentPlanDate}
              isExpanded={expandedBatchId === batch.id}
              ovenOccupied={running.length > 0 || hasExternalActive || ovenQueuePosition(ovenQueues, batch) !== 1}
              onToggle={() => onToggleBatch(batch.id)}
              onStart={(id) => onStart(id, mid)}
              onUnassign={onUnassign}
              onPackage={onPackage}
              onLoaderNameChange={onLoaderNameChange}
              onRoastTimeChange={onRoastTimeChange}
            />
          ))}
          {/* Completed */}
          {done.map((batch) => (
            <CompletedBatchItem
              key={batch.id}
              batch={batch}
              isExpanded={expandedBatchId === batch.id}
              onToggle={() => onToggleBatch(batch.id)}
              onBatchUpdated={onBatchUpdated}
              onPackage={onPackage}
            />
          ))}
        </div>
      )}
      {items.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-100 text-base text-gray-600 font-medium shrink-0">
          {running.length > 0 && <span className="text-yellow-600">{running.length} בביצוע</span>}
          {running.length > 0 && assigned.length > 0 && <span className="mx-1">·</span>}
          {assigned.length > 0 && <span className="text-blue-600">{assigned.length} ממתינים</span>}
          {(running.length > 0 || assigned.length > 0) && done.length > 0 && <span className="mx-1">·</span>}
          {done.length > 0 && <span className="text-green-700">{done.length} הושלמו</span>}
          {done.some((b) => isUnknown(b)) && (
            <span className="mr-2 text-orange-600">(כולל batchs ללא תערובת)</span>
          )}
        </div>
      )}
    </div>
  );
}

// ── Add to Plan Modal ────────────────────────────────────────────────

function AddToPlanModal({
  blend,
  onClose,
  onAdd,
}: {
  blend: Blend;
  onClose: () => void;
  onAdd: (blendId: string, quantity: number, unit: string, batchWeightKg: number, roastTimeSeconds: number, loadingWorkerName?: string, packaging?: PackagingInstructionInput) => void;
}) {
  const [quantity, setQuantity] = useState<number>(1);
  const [weightMode, setWeightMode] = useState<'90' | '70' | 'custom'>('90');
  const [customWeight, setCustomWeight] = useState<number>(90);
  const [roastMinutes, setRoastMinutes] = useState(Math.floor(blend.required_roast_time_seconds / 60));
  const [roastSeconds, setRoastSeconds] = useState(blend.required_roast_time_seconds % 60);
  const [loadingWorkerName, setLoadingWorkerName] = useState('');
  const [attachPackaging, setAttachPackaging] = useState(false);
  const [templates, setTemplates] = useState<PackageTemplate[]>([]);
  const [packageMode, setPackageMode] = useState<'template' | 'custom'>('template');
  const [templateId, setTemplateId] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [bagType, setBagType] = useState('');
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [packageQuantityMode, setPackageQuantityMode] = useState<'all' | 'bags'>('bags');
  const [packageBagCount, setPackageBagCount] = useState('1');
  const packageBagCountValid = isValidPackagingQuantity(packageBagCount, 10000);
  const matchingTemplates = templates.filter(item => !item.blend_id || item.blend_id === blend.id);
  const batchWeightKg = weightMode === 'custom' ? customWeight : Number(weightMode);
  const roastTimeSeconds = roastMinutes * 60 + roastSeconds;
  const maxQuantity = attachPackaging && packageQuantityMode === 'bags' ? 33 : 50;
  useEffect(() => { listPackageTemplates().then(items => {
    setTemplates(items); const first = items.find(item => !item.blend_id || item.blend_id === blend.id); setTemplateId(first?.id ?? ''); if (!first) setPackageMode('custom');
  }).catch(() => {}); }, [blend.id]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (quantity <= 0 || !Number.isInteger(quantity) || quantity > maxQuantity) return;
    let packaging: PackagingInstructionInput | undefined;
    if (attachPackaging) {
      if (packageQuantityMode === 'bags' && !packageBagCountValid) return;
      const packagingQuantity = packageQuantityMode === 'all'
        ? { all_batch: true }
        : { all_batch: false, bag_count: Number(packageBagCount) };
      if (packageMode === 'template') {
        if (!templateId) return;
        packaging = { template_id: templateId, ...packagingQuantity };
      } else {
        if (!customerName.trim() || !bagType.trim()) return;
        packaging = { customer_name: customerName.trim(), bag_type: bagType.trim(), ...packagingQuantity, save_as_template: saveAsTemplate };
      }
    }
    if (!Number.isInteger(roastTimeSeconds) || roastTimeSeconds < 1 || roastTimeSeconds > MAX_ROAST_TIME_SECONDS) return;
    onAdd(blend.id, quantity, 'batches', batchWeightKg, roastTimeSeconds, loadingWorkerName.trim() || undefined, packaging);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl p-4 w-full max-w-lg max-h-[90vh] overflow-y-auto mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-xl font-bold text-gray-800 mb-1 text-center break-words line-clamp-1" title={blend.name}>{blend.name}</h3>
        <p className="text-sm text-gray-500 text-center mb-3">
          זמן קלייה: {formatTime(blend.required_roast_time_seconds)}
        </p>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block font-medium text-gray-700 mb-1">כמות</label>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                className="w-12 h-11 flex items-center justify-center text-2xl font-bold rounded-xl border-2 border-gray-300 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 transition-colors touch-manipulation select-none shrink-0"
              >
                −
              </button>
              <input
                type="number"
                step={1}
                min={1}
                max={maxQuantity}
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, Number(e.target.value)))}
                className="min-w-0 flex-1 px-3 py-2 text-2xl font-bold text-center border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
                style={{ MozAppearance: 'textfield' } as React.CSSProperties}
              />
              <button
                type="button"
                onClick={() => setQuantity((q) => Math.min(maxQuantity, q + 1))}
                disabled={quantity >= maxQuantity}
                className="w-12 h-11 flex items-center justify-center text-2xl font-bold rounded-xl border-2 border-amber-400 bg-amber-50 hover:bg-amber-100 active:bg-amber-200 transition-colors touch-manipulation select-none shrink-0"
              >
                +
              </button>
            </div>
            <p className="mt-1 text-xs text-gray-500">עד {maxQuantity} אצוות בכל הוספה. הוראות וכמות שקיות יחולו על כל אצווה בנפרד.</p>
          </div>

          <div>
            <label className="block font-medium text-gray-700 mb-1">משקל אצווה</label>
            <div className="grid grid-cols-3 gap-2" dir="rtl">
              <button type="button" onClick={() => setWeightMode('90')} className={`py-2 rounded-xl font-bold border-2 ${weightMode === '90' ? 'bg-amber-600 border-amber-600 text-white' : 'bg-white border-gray-300 text-gray-700'}`}>90 ק״ג</button>
              <button type="button" onClick={() => setWeightMode('70')} className={`py-2 rounded-xl font-bold border-2 ${weightMode === '70' ? 'bg-amber-600 border-amber-600 text-white' : 'bg-white border-gray-300 text-gray-700'}`}>70 ק״ג</button>
              <button type="button" onClick={() => setWeightMode('custom')} className={`py-2 rounded-xl font-bold border-2 ${weightMode === 'custom' ? 'bg-amber-600 border-amber-600 text-white' : 'bg-white border-gray-300 text-gray-700'}`}>מותאם</button>
            </div>
            {weightMode === 'custom' && (
              <div className="mt-2 flex items-center gap-2" dir="rtl">
                <input
                  type="number"
                  min={1}
                  max={90}
                  step={1}
                  value={customWeight}
                  onChange={e => setCustomWeight(Number(e.target.value))}
                  className="min-w-0 flex-1 px-3 py-2 text-xl font-bold text-center border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 outline-none"
                />
                <span className="font-bold text-gray-600">ק״ג</span>
              </div>
            )}
            <p className="mt-1 text-xs text-gray-500">המתכון יחושב באופן יחסי ממתכון הבסיס של 90 ק״ג.</p>
          </div>

          <div>
            <label className="block font-medium text-gray-700 mb-1">זמן קלייה</label>
            <div className="grid grid-cols-2 gap-3" dir="rtl">
              <label className="text-sm font-bold text-gray-600">
                דקות
                <input
                  type="number"
                  min={0}
                  max={1440}
                  value={roastMinutes}
                  onChange={event => setRoastMinutes(Number(event.target.value))}
                  className="mt-1 w-full rounded-xl border-2 border-gray-300 px-3 py-2 text-center text-xl font-bold focus:border-amber-500 focus:ring-2 focus:ring-amber-500"
                />
              </label>
              <label className="text-sm font-bold text-gray-600">
                שניות
                <input
                  type="number"
                  min={0}
                  max={59}
                  value={roastSeconds}
                  onChange={event => setRoastSeconds(Number(event.target.value))}
                  className="mt-1 w-full rounded-xl border-2 border-gray-300 px-3 py-2 text-center text-xl font-bold focus:border-amber-500 focus:ring-2 focus:ring-amber-500"
                />
              </label>
            </div>
            <p className="mt-1 text-xs text-gray-500">הזמן יחול על כל האצוות שייווצרו בהוספה זו.</p>
          </div>

          <div>
            <div className="block font-medium text-gray-700 mb-1">
              שם המעמיס <span className="font-normal text-gray-400">(אופציונלי)</span>
            </div>
            <LoadingWorkerNamePicker
              idPrefix="new-batch-loader-name"
              value={loadingWorkerName}
              onChange={setLoadingWorkerName}
            />
            {quantity > 1 && (
              <p className="mt-1 text-xs text-gray-500">השם יחול על כל {quantity} האצוות שייווצרו.</p>
            )}
          </div>

          <div dir="rtl" className="border-2 border-purple-200 rounded-xl p-3 bg-purple-50">
            <label className="flex items-center gap-2 font-bold"><input type="checkbox" checked={attachPackaging} onChange={e => setAttachPackaging(e.target.checked)} className="w-5 h-5 accent-purple-700" />צרף הוראת אריזה</label>
            {attachPackaging && <div className="space-y-2 mt-2">
              <div className="grid grid-cols-2 gap-2"><button type="button" onClick={() => setPackageMode('template')} className={`py-2 rounded-lg font-bold ${packageMode === 'template' ? 'bg-purple-700 text-white' : 'bg-white border'}`}>שמורה</button><button type="button" onClick={() => setPackageMode('custom')} className={`py-2 rounded-lg font-bold ${packageMode === 'custom' ? 'bg-purple-700 text-white' : 'bg-white border'}`}>חדשה</button></div>
              {packageMode === 'template' ? <select value={templateId} onChange={e => setTemplateId(e.target.value)} className="w-full px-3 py-2 border-2 rounded-lg bg-white"><option value="">בחר הוראה</option>{matchingTemplates.map(item => <option key={item.id} value={item.id}>{item.customer_name} — {item.bag_type}</option>)}</select> : <><input value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder="שם לקוח" className="w-full px-3 py-2 border-2 rounded-lg" /><textarea value={bagType} onChange={e => setBagType(e.target.value)} placeholder="סוג שקית / הוראות" rows={2} className="w-full px-3 py-2 border-2 rounded-lg" /><label className="flex gap-2 items-center font-bold"><input type="checkbox" checked={saveAsTemplate} onChange={e => setSaveAsTemplate(e.target.checked)} className="w-5 h-5 accent-purple-700" />שמור לשימוש עתידי</label></>}
              <div>
                <label className="block text-sm font-bold mb-1">כמות לאריזה בכל אצווה</label>
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" onClick={() => setPackageQuantityMode('all')} className={`py-2 rounded-lg font-bold ${packageQuantityMode === 'all' ? 'bg-purple-700 text-white' : 'bg-white border-2'}`}>כל האצווה</button>
                  <button type="button" onClick={() => setPackageQuantityMode('bags')} className={`py-2 rounded-lg font-bold ${packageQuantityMode === 'bags' ? 'bg-purple-700 text-white' : 'bg-white border-2'}`}>מספר שקיות</button>
                </div>
                {packageQuantityMode === 'bags' && <div className="mt-2 flex items-center gap-2">
                  <button type="button" onClick={() => setPackageBagCount(count => String(Math.max(1, Number(count) - 1)))} disabled={!packageBagCountValid || Number(packageBagCount) <= 1} className="w-11 shrink-0 py-1 rounded-lg border-2 bg-white text-xl font-bold disabled:opacity-40">−</button>
                  <PackagingQuantityCombobox value={packageBagCount} onChange={setPackageBagCount} max={10000} label="מספר שקיות לאריזה בכל אצווה" compact />
                  <button type="button" onClick={() => setPackageBagCount(count => String(Math.min(10000, Number(count) + 1)))} disabled={!packageBagCountValid || Number(packageBagCount) >= 10000} className="w-11 shrink-0 py-1 rounded-lg border-2 bg-white text-xl font-bold disabled:opacity-40">+</button>
                </div>}
              </div>
            </div>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={onClose}
              className="py-2 font-bold text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
            >
              ביטול
            </button>
            <button
              type="submit"
              disabled={quantity <= 0 || !Number.isInteger(quantity) || quantity > maxQuantity || !Number.isInteger(batchWeightKg) || batchWeightKg < 1 || batchWeightKg > 90 || !Number.isInteger(roastMinutes) || !Number.isInteger(roastSeconds) || roastSeconds < 0 || roastSeconds > 59 || roastTimeSeconds < 1 || roastTimeSeconds > MAX_ROAST_TIME_SECONDS || (attachPackaging && ((packageQuantityMode === 'bags' && !packageBagCountValid) || (packageMode === 'template' ? !templateId : (!customerName.trim() || !bagType.trim()))))}
              className="py-2 font-bold text-white bg-green-600 rounded-xl hover:bg-green-700 disabled:bg-gray-300 transition-colors"
            >
              הוסף לתכנית
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Blend Management Modal ───────────────────────────────────────────

function BlendModal({
  blend,
  onClose,
  onSave,
}: {
  blend: Blend | null;
  onClose: () => void;
  onSave: (
    name: string,
    seconds: number,
    targetColorRgb: [number, number, number] | null,
    targetRoastScore: number | null,
    recipe: BlendRecipe | null,
  ) => void;
}) {
  const [name, setName] = useState(blend?.name || '');
  const [minutes, setMinutes] = useState(blend ? Math.floor(blend.required_roast_time_seconds / 60) : 12);
  const [seconds, setSeconds] = useState(blend ? blend.required_roast_time_seconds % 60 : 0);
  const [colorScore, setColorScore] = useState<number>(
    blend?.target_roast_score != null
      ? blend.target_roast_score
      : blend?.target_color_rgb
        ? rgbToScore(blend.target_color_rgb)
        : 50,
  );
  const [hasTargetColor, setHasTargetColor] = useState(!!blend?.target_color_rgb);
  const [targetRoastScore, setTargetRoastScore] = useState<number | null>(
    blend?.target_roast_score ?? null,
  );
  const [hasRecipe, setHasRecipe] = useState(!!blend?.recipe);
  const [recipeRows, setRecipeRows] = useState(
    blend?.recipe?.ingredients.map(row => ({ ...row })) ?? [
      { name: '', amount_kg: 0, cumulative_kg: 0 },
    ],
  );
  const recipeAmountTotal = recipeRows.reduce((total, row) => total + (Number(row.amount_kg) || 0), 0);
  const recipeHasWarning = hasRecipe && (
    Math.abs(recipeAmountTotal - 90) > 0.001
    || recipeRows.some((row, index) =>
      row.cumulative_kg <= 0
      || (index > 0 && row.cumulative_kg < recipeRows[index - 1].cumulative_kg)
    )
    || Math.abs((recipeRows[recipeRows.length - 1]?.cumulative_kg ?? 0) - 90) > 0.001
  );
  const updateRecipeRow = (index: number, updates: Partial<(typeof recipeRows)[number]>) => {
    setRecipeRows(rows => rows.map((row, rowIndex) => rowIndex === index ? { ...row, ...updates } : row));
  };
  const moveRecipeRow = (index: number, direction: -1 | 1) => {
    const next = index + direction;
    if (next < 0 || next >= recipeRows.length) return;
    setRecipeRows(rows => {
      const copy = [...rows];
      [copy[index], copy[next]] = [copy[next], copy[index]];
      return copy;
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const total = minutes * 60 + seconds;
    if (!name || total <= 0) return;
    if (hasRecipe && recipeRows.some(row => !row.name.trim() || row.amount_kg <= 0 || row.cumulative_kg <= 0)) return;
    onSave(
      name,
      total,
      hasTargetColor ? scoreToRgb(colorScore) : null,
      targetRoastScore,
      hasRecipe
        ? {
            total_kg: 90,
            ingredients: recipeRows.map(row => ({ ...row, name: row.name.trim() })),
          }
        : null,
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">
          {blend ? 'עריכת תערובת' : 'תערובת חדשה'}
        </h3>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-lg font-medium text-gray-700 mb-2">שם</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              required
              className="w-full px-4 py-3 text-xl border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
            />
          </div>
          <div className="rounded-xl border-2 border-amber-200 bg-amber-50 p-4">
            <label className="flex items-center gap-2 text-lg font-bold text-gray-700">
              <input
                type="checkbox"
                checked={hasRecipe}
                onChange={(e) => setHasRecipe(e.target.checked)}
                className="h-5 w-5 accent-amber-700"
              />
              מתכון טעינה ל-90 ק״ג
            </label>
            {hasRecipe && (
              <div className="mt-3 space-y-2">
                {recipeRows.map((row, index) => (
                  <div key={index} className="grid grid-cols-[minmax(0,1fr)_5rem_5rem_auto] items-center gap-2">
                    <input
                      value={row.name}
                      onChange={e => updateRecipeRow(index, { name: e.target.value })}
                      placeholder="רכיב"
                      required
                      className="min-w-0 rounded-lg border-2 border-gray-300 px-2 py-2"
                    />
                    <input
                      type="number"
                      min="0.1"
                      step="0.1"
                      value={row.amount_kg || ''}
                      onChange={e => updateRecipeRow(index, { amount_kg: Number(e.target.value) })}
                      placeholder="ק״ג"
                      required
                      className="min-w-0 rounded-lg border-2 border-gray-300 px-2 py-2 text-center"
                    />
                    <input
                      type="number"
                      min="0.1"
                      step="0.1"
                      value={row.cumulative_kg || ''}
                      onChange={e => updateRecipeRow(index, { cumulative_kg: Number(e.target.value) })}
                      placeholder="מצטבר"
                      required
                      className="min-w-0 rounded-lg border-2 border-gray-300 px-2 py-2 text-center"
                    />
                    <div className="flex">
                      <button type="button" onClick={() => moveRecipeRow(index, -1)} disabled={index === 0} className="px-1 text-gray-500 disabled:opacity-25" aria-label="העבר רכיב למעלה">↑</button>
                      <button type="button" onClick={() => moveRecipeRow(index, 1)} disabled={index === recipeRows.length - 1} className="px-1 text-gray-500 disabled:opacity-25" aria-label="העבר רכיב למטה">↓</button>
                      <button
                        type="button"
                        onClick={() => setRecipeRows(rows => rows.filter((_, rowIndex) => rowIndex !== index))}
                        disabled={recipeRows.length === 1}
                        className="px-1 text-red-500 disabled:opacity-25"
                        aria-label="מחק רכיב"
                      >✕</button>
                    </div>
                  </div>
                ))}
                <div className="flex items-center justify-between gap-2">
                  <button
                    type="button"
                    onClick={() => setRecipeRows(rows => [...rows, { name: '', amount_kg: 0, cumulative_kg: 0 }])}
                    className="rounded-lg bg-amber-100 px-3 py-2 text-sm font-bold text-amber-800 hover:bg-amber-200"
                  >
                    + רכיב
                  </button>
                  <span className="text-sm font-semibold">סכום רכיבים: {Number(recipeAmountTotal.toFixed(1))} ק״ג</span>
                </div>
                {recipeHasWarning && (
                  <p role="status" className="rounded-lg bg-orange-100 px-3 py-2 text-sm font-semibold text-orange-800">
                    ⚠ הסכום או הערכים המצטברים אינם תואמים ל-90 ק״ג. ניתן לשמור כך אם זה המתכון המקורי.
                  </p>
                )}
              </div>
            )}
          </div>
          <div>
            <label className="block text-lg font-medium text-gray-700 mb-2">זמן קלייה</label>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setMinutes((m) => Math.max(0, m - 1))}
                className="w-14 h-14 flex items-center justify-center text-2xl font-bold rounded-xl border-2 border-gray-300 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 transition-colors touch-manipulation select-none shrink-0">−</button>
              <input
                type="number"
                min={0}
                value={minutes}
                onChange={(e) => setMinutes(Math.max(0, Number(e.target.value)))}
                className="w-20 px-2 py-3 text-xl text-center border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
              />
              <button type="button" onClick={() => setMinutes((m) => m + 1)}
                className="w-14 h-14 flex items-center justify-center text-2xl font-bold rounded-xl border-2 border-amber-400 bg-amber-50 hover:bg-amber-100 active:bg-amber-200 transition-colors touch-manipulation select-none shrink-0">+</button>
              <span className="text-lg text-gray-500 mx-1">דק׳</span>
              <button type="button" onClick={() => setSeconds((s) => Math.max(0, s - 5))}
                className="w-14 h-14 flex items-center justify-center text-2xl font-bold rounded-xl border-2 border-gray-300 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 transition-colors touch-manipulation select-none shrink-0">−</button>
              <input
                type="number"
                min={0}
                max={59}
                value={seconds}
                onChange={(e) => setSeconds(Math.min(59, Math.max(0, Number(e.target.value))))}
                className="w-20 px-2 py-3 text-xl text-center border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
              />
              <button type="button" onClick={() => setSeconds((s) => Math.min(59, s + 5))}
                className="w-14 h-14 flex items-center justify-center text-2xl font-bold rounded-xl border-2 border-amber-400 bg-amber-50 hover:bg-amber-100 active:bg-amber-200 transition-colors touch-manipulation select-none shrink-0">+</button>
              <span className="text-lg text-gray-500 mx-1">שנ׳</span>
            </div>
          </div>
          <div>
            <label className="block text-lg font-medium text-gray-700 mb-2">צבע יעד</label>
            <div className="flex items-center gap-3 mb-2">
              <input
                type="checkbox"
                checked={hasTargetColor}
                onChange={(e) => setHasTargetColor(e.target.checked)}
                className="w-5 h-5 accent-amber-600"
                id="target-color-toggle"
              />
              <label htmlFor="target-color-toggle" className="text-base text-gray-600">הגדר צבע יעד</label>
            </div>
            {hasTargetColor && (
              <div className="mt-1">
                <div className="relative h-10 rounded-lg overflow-hidden border border-gray-300"
                  style={{ background: `linear-gradient(to right, ${ROAST_LEVELS.map((l) => l.color).join(', ')})` }}
                >
                  <input
                    type="range"
                    min={0}
                    max={100}
                    value={colorScore}
                    onChange={(e) => setColorScore(Number(e.target.value))}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                  <div
                    className="absolute top-0 bottom-0 w-1 bg-white/80 shadow-lg pointer-events-none rounded-full"
                    style={{ left: `calc(${colorScore}% - 2px)` }}
                  />
                </div>
                <div className="flex items-center gap-3 mt-2">
                  <div
                    className="w-8 h-8 rounded-lg border-2 border-gray-300 shrink-0"
                    style={{ backgroundColor: `rgb(${scoreToRgb(colorScore).join(',')})` }}
                  />
                  <span className="text-base font-semibold text-gray-700">
                    {roastLevelLabel(colorScore).label}
                  </span>
                  <span className="text-sm text-gray-500">({colorScore}/100)</span>
                </div>
              </div>
            )}
          </div>
          <div>
            <label className="block text-lg font-medium text-gray-700 mb-2">יעד סקאלת קלייה (0–100)</label>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={targetRoastScore !== null}
                onChange={(e) => setTargetRoastScore(e.target.checked ? 50 : null)}
                className="w-5 h-5 accent-amber-600"
                id="target-score-toggle"
              />
              <label htmlFor="target-score-toggle" className="text-base text-gray-600">הגדר יעד קלייה</label>
              {targetRoastScore !== null && (
                <>
                  <button type="button" onClick={() => setTargetRoastScore((s) => Math.max(10, (s ?? 50) - 5))}
                    className="w-10 h-10 flex items-center justify-center text-xl font-bold rounded-xl border-2 border-gray-300 bg-gray-100 hover:bg-gray-200 transition-colors">−</button>
                  <input
                    type="number"
                    min={10}
                    max={100}
                    value={targetRoastScore}
                    onChange={(e) => setTargetRoastScore(Math.min(100, Math.max(10, Number(e.target.value))))}
                    className="w-20 px-2 py-2 text-xl text-center border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
                  />
                  <button type="button" onClick={() => setTargetRoastScore((s) => Math.min(100, (s ?? 50) + 5))}
                    className="w-10 h-10 flex items-center justify-center text-xl font-bold rounded-xl border-2 border-amber-400 bg-amber-50 hover:bg-amber-100 transition-colors">+</button>
                </>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 pt-2">
            <button type="button" onClick={onClose} className="py-3 text-lg font-bold text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors">
              ביטול
            </button>
            <button type="submit" className="py-3 text-lg font-bold text-white bg-amber-700 rounded-xl hover:bg-amber-800 transition-colors">
              {blend ? 'עדכן' : 'הוסף'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Package Management ──────────────────────────────────────────────

function PackageManagementModal({ onClose }: { onClose: () => void }) {
  const [templates, setTemplates] = useState<PackageTemplate[]>([]);
  const [availableBlends, setAvailableBlends] = useState<Blend[]>([]);
  const [editing, setEditing] = useState<PackageTemplate | null>(null);
  const [customerName, setCustomerName] = useState('');
  const [blendId, setBlendId] = useState('');
  const [bagType, setBagType] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    try { setTemplates(await listPackageTemplates()); }
    catch (err) { setError(mutationErrorMessage(err)); }
  }, []);
  useEffect(() => { reload(); listBlends().then(setAvailableBlends).catch(() => {}); }, [reload]);

  const reset = () => { setEditing(null); setCustomerName(''); setBlendId(''); setBagType(''); setError(''); };
  const startEdit = (template: PackageTemplate) => {
    setEditing(template); setCustomerName(template.customer_name); setBlendId(template.blend_id ?? ''); setBagType(template.bag_type); setError('');
  };
  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!customerName.trim() || !blendId || !bagType.trim()) return;
    setSaving(true); setError('');
    try {
      if (editing) await updatePackageTemplate(editing.id, { customer_name: customerName.trim(), blend_id: blendId, bag_type: bagType.trim() });
      else await createPackageTemplate(customerName.trim(), blendId, bagType.trim());
      reset(); await reload();
    } catch (err) { setError(mutationErrorMessage(err)); }
    finally { setSaving(false); }
  };
  const remove = async (template: PackageTemplate) => {
    if (!confirmWithoutLeavingFullscreen(`למחוק את הוראת האריזה עבור ${template.customer_name}?`)) return;
    try { await deletePackageTemplate(template.id); if (editing?.id === template.id) reset(); await reload(); }
    catch (err) { setError(mutationErrorMessage(err)); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div dir="rtl" className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-2xl font-bold text-gray-800">ניהול אריזות</h3>
          <button onClick={onClose} className="text-2xl text-gray-500 p-2">✕</button>
        </div>
        <form onSubmit={save} className="grid md:grid-cols-3 gap-3 bg-purple-50 border border-purple-200 rounded-xl p-4 mb-5">
          <input value={customerName} onChange={e => setCustomerName(e.target.value)} required placeholder="שם לקוח" className="px-4 py-3 border-2 border-gray-300 rounded-xl outline-none focus:border-purple-500" />
          <select value={blendId} onChange={e => setBlendId(e.target.value)} required className="px-4 py-3 border-2 border-gray-300 rounded-xl bg-white"><option value="">בחר תערובת</option>{availableBlends.map(blend => <option key={blend.id} value={blend.id}>{blend.name}</option>)}</select>
          <textarea value={bagType} onChange={e => setBagType(e.target.value)} required placeholder="סוג שקית / הוראות" rows={2} className="px-4 py-3 border-2 border-gray-300 rounded-xl outline-none focus:border-purple-500" />
          <div className="md:col-span-3 flex gap-3 justify-end">
            {editing && <button type="button" onClick={reset} className="px-4 py-2 bg-gray-200 rounded-xl font-bold">ביטול עריכה</button>}
            <button disabled={saving || !customerName.trim() || !blendId || !bagType.trim()} className="px-5 py-2 bg-purple-700 text-white rounded-xl font-bold disabled:bg-gray-300">{saving ? 'שומר…' : editing ? 'עדכן' : '+ הוסף'}</button>
          </div>
        </form>
        {error && <div role="alert" className="mb-4 text-red-700 font-bold">{error}</div>}
        <div className="space-y-3">
          {templates.map(template => (
            <div key={template.id} className="flex items-start justify-between gap-3 border rounded-xl p-4">
              <div className="min-w-0"><div className="font-bold text-lg">{template.customer_name}</div><div className="text-sm text-purple-700">{template.blend_name || 'תערובת טרם הוגדרה'}</div><div className="text-gray-600 whitespace-pre-wrap">{template.bag_type}</div></div>
              <div className="flex shrink-0"><button onClick={() => startEdit(template)} className="p-2">✏️</button><button onClick={() => remove(template)} className="p-2">🗑</button></div>
            </div>
          ))}
          {!templates.length && <p className="text-center text-gray-400 py-5">עדיין אין הוראות אריזה שמורות.</p>}
        </div>
      </div>
    </div>
  );
}

function PackageAssignmentModal({ initialBatches, onClose, onBatchUpdated }: { initialBatches: RoastBatch[]; onClose: () => void; onBatchUpdated: (batch: RoastBatch) => void }) {
  const [modalBatches, setModalBatches] = useState(initialBatches);
  const [batchId, setBatchId] = useState(initialBatches[0]?.id ?? '');
  const [templates, setTemplates] = useState<PackageTemplate[]>([]);
  const [mode, setMode] = useState<'template' | 'custom'>('template');
  const [templateId, setTemplateId] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [comment, setComment] = useState('');
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [quantityMode, setQuantityMode] = useState<'all' | 'bags'>('bags');
  const [bagCount, setBagCount] = useState('1');
  const bagCountValid = isValidPackagingQuantity(bagCount, 100);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const batch = modalBatches.find(item => item.id === batchId) ?? modalBatches[0];

  useEffect(() => {
    listPackageTemplates().then(items => {
      setTemplates(items);
      const firstMatch = items.find(item => !item.blend_id || item.blend_id === initialBatches[0]?.blend_id);
      setTemplateId(firstMatch?.id ?? '');
      if (!firstMatch) setMode('custom');
    }).catch(err => setError(mutationErrorMessage(err)));
  }, []);
  const acceptUpdate = (updated: RoastBatch) => {
    setModalBatches(items => items.map(item => item.id === updated.id ? updated : item));
    onBatchUpdated(updated);
  };
  const reset = () => {
    setEditingId(null);
    setMode(templates.length ? 'template' : 'custom');
    setTemplateId(templates[0]?.id ?? '');
    setCustomerName(''); setComment(''); setSaveAsTemplate(false);
    setQuantityMode('bags'); setBagCount('1');
    setError('');
  };
  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!batch) return;
    if (quantityMode === 'bags' && !bagCountValid) return;
    if (mode === 'template' && !templateId) return;
    if (mode === 'custom' && (!customerName.trim() || !comment.trim())) return;
    setSaving(true); setError('');
    try {
      if (mode === 'custom' && saveAsTemplate && !editingId) {
        try { await createPackageTemplate(customerName.trim(), batch.blend_id, comment.trim()); }
        catch (err) { setError(mutationErrorMessage(err)); }
      }
      const quantity = quantityMode === 'all'
        ? { all_batch: true }
        : { all_batch: false, bag_count: Number(bagCount) };
      let updated: RoastBatch;
      if (editingId) {
        updated = mode === 'template'
          ? await updatePackageRequirement(batch.id, editingId, { template_id: templateId, ...quantity })
          : await updatePackageRequirement(batch.id, editingId, { customer_name: customerName.trim(), comment: comment.trim(), ...quantity });
      } else {
        updated = mode === 'template'
          ? await createPackageRequirement(batch.id, { template_id: templateId, ...quantity })
          : await createPackageRequirement(batch.id, { customer_name: customerName.trim(), comment: comment.trim(), ...quantity });
      }
      acceptUpdate(updated);
      if (mode === 'custom' && saveAsTemplate && !editingId) {
        listPackageTemplates().then(setTemplates).catch(() => {});
      }
      reset();
    } catch (err) { setError(mutationErrorMessage(err)); }
    finally { setSaving(false); }
  };
  const edit = (requirement: NonNullable<RoastBatch['package_requirements']>[number]) => {
    setEditingId(requirement.id); setError('');
    setQuantityMode(requirement.all_batch ? 'all' : 'bags');
    setBagCount(String(requirement.bag_count ?? 1));
    if (requirement.template_id) {
      setMode('template'); setTemplateId(requirement.template_id);
    } else {
      setMode('custom'); setCustomerName(requirement.customer_name); setComment(requirement.comment); setSaveAsTemplate(false);
    }
  };
  const remove = async (requirementId: string) => {
    if (!batch || !confirmWithoutLeavingFullscreen('למחוק הוראת אריזה זו מהאצווה?')) return;
    try { acceptUpdate(await deletePackageRequirement(batch.id, requirementId)); if (editingId === requirementId) reset(); }
    catch (err) { setError(mutationErrorMessage(err)); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div dir="rtl" className="bg-white rounded-2xl shadow-2xl p-4 w-full max-w-2xl mx-4 max-h-[calc(100vh-1rem)] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3"><h3 className="text-xl font-bold">הוראות אריזה לאצווה</h3><button onClick={onClose} className="text-xl text-gray-500 p-1">✕</button></div>
        {modalBatches.length > 1 && (
          <div className="mb-4"><label className="block font-bold mb-2">בחר אצווה</label><select value={batchId} onChange={e => { setBatchId(e.target.value); reset(); }} className="w-full px-4 py-3 border-2 rounded-xl">
            {modalBatches.map((item, index) => <option key={item.id} value={item.id}>{index + 1}. {item.blend_name}</option>)}
          </select></div>
        )}
        <form onSubmit={save} className="space-y-2 bg-purple-50 border border-purple-200 rounded-xl p-3">
          <div className="flex gap-2">
            <button type="button" onClick={() => setMode('template')} disabled={!!editingId} className={`flex-1 px-4 py-2 rounded-xl font-bold ${mode === 'template' ? 'bg-purple-700 text-white' : 'bg-white border-2'} disabled:opacity-50`}>מהוראה שמורה</button>
            <button type="button" onClick={() => setMode('custom')} disabled={!!editingId} className={`flex-1 px-4 py-2 rounded-xl font-bold ${mode === 'custom' ? 'bg-purple-700 text-white' : 'bg-white border-2'} disabled:opacity-50`}>הודעה מותאמת</button>
          </div>
          {mode === 'template' ? (
            <div><label className="block font-bold mb-2">לקוח והוראת אריזה</label><select value={templateId} onChange={e => setTemplateId(e.target.value)} required className="w-full px-4 py-3 border-2 rounded-xl bg-white"><option value="">-- בחר הוראה --</option>{templates.filter(item => !item.blend_id || item.blend_id === batch?.blend_id).map(item => <option key={item.id} value={item.id}>{item.customer_name} — {item.bag_type}</option>)}</select></div>
          ) : (
            <>
              <div><label className="block font-bold mb-2">שם לקוח</label><input value={customerName} onChange={e => setCustomerName(e.target.value)} required placeholder="שם לקוח" className="w-full px-4 py-3 border-2 rounded-xl bg-white outline-none focus:border-purple-500" /></div>
              <div><label className="block font-bold mb-2">הערה לגבי האריזות</label><textarea value={comment} onChange={e => setComment(e.target.value)} required placeholder="הערה לגבי האריזות" rows={2} className="w-full px-4 py-3 border-2 rounded-xl bg-white outline-none focus:border-purple-500" /></div>
              {!editingId && <label className="flex items-center gap-2 font-bold"><input type="checkbox" checked={saveAsTemplate} onChange={e => setSaveAsTemplate(e.target.checked)} className="w-5 h-5 accent-purple-700" />שמור כהוראה לשימוש חוזר</label>}
            </>
          )}
          <div>
            <label className="block font-bold mb-1">כמות לאריזה</label>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setQuantityMode('all')} className={`py-2 rounded-xl font-bold ${quantityMode === 'all' ? 'bg-purple-700 text-white' : 'bg-white border-2'}`}>כל האצווה</button>
              <button type="button" onClick={() => setQuantityMode('bags')} className={`py-2 rounded-xl font-bold ${quantityMode === 'bags' ? 'bg-purple-700 text-white' : 'bg-white border-2'}`}>מספר שקיות</button>
            </div>
            {quantityMode === 'bags' && <div className="mt-2 flex items-center gap-2">
              <button type="button" onClick={() => setBagCount(count => String(Math.max(1, Number(count) - 1)))} disabled={!bagCountValid || Number(bagCount) <= 1} className="w-12 shrink-0 py-2 rounded-xl border-2 bg-white text-xl font-bold disabled:opacity-40">−</button>
              <PackagingQuantityCombobox value={bagCount} onChange={setBagCount} max={100} label="מספר שקיות לאריזה באצווה" />
              <button type="button" onClick={() => setBagCount(count => String(Math.min(100, Number(count) + 1)))} disabled={!bagCountValid || Number(bagCount) >= 100} className="w-12 shrink-0 py-2 rounded-xl border-2 bg-white text-xl font-bold disabled:opacity-40">+</button>
            </div>}
          </div>
          <div className="flex justify-end gap-3">{editingId && <button type="button" onClick={reset} className="px-4 py-2 bg-gray-200 rounded-xl font-bold">ביטול עריכה</button>}<button disabled={saving || (quantityMode === 'bags' && !bagCountValid) || (mode === 'template' ? !templateId : (!customerName.trim() || !comment.trim()))} className="px-5 py-2 bg-purple-700 text-white rounded-xl font-bold disabled:bg-gray-300">{saving ? 'שומר…' : editingId ? 'עדכן' : '+ שייך לאצווה'}</button></div>
        </form>
        {error && <div role="alert" className="mt-3 text-red-700 font-bold">{error}</div>}
        <div className="mt-5 space-y-3">
          {(batch?.package_requirements ?? []).map(requirement => <div key={requirement.id} className="flex justify-between gap-3 border rounded-xl p-4"><div><div className="font-bold">{requirement.customer_name} · {packagingQuantityLabel(requirement)}</div><div className="text-gray-600 whitespace-pre-wrap">{requirement.comment}</div></div><div className="flex shrink-0"><button onClick={() => edit(requirement)} className="p-2">✏️</button><button onClick={() => remove(requirement.id)} className="p-2">🗑</button></div></div>)}
          {!(batch?.package_requirements ?? []).length && <p className="text-center text-gray-400 py-3">אין הוראות אריזה לאצווה זו.</p>}
        </div>
        <button onClick={onClose} className="mt-5 w-full py-3 bg-gray-100 rounded-xl font-bold">סיום / דלג</button>
      </div>
    </div>
  );
}

function PackagingJobModal({ planDate, blends, job, onClose, onSaved }: {
  planDate: string;
  blends: Blend[];
  job: PackagingJob | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [templates, setTemplates] = useState<PackageTemplate[]>([]);
  const [mode, setMode] = useState<'template' | 'custom'>(job ? 'custom' : 'template');
  const [blendId, setBlendId] = useState(job?.blend_id ?? blends[0]?.id ?? '');
  const [templateId, setTemplateId] = useState('');
  const [customerName, setCustomerName] = useState(job?.customer_name ?? '');
  const [bagType, setBagType] = useState(job?.bag_type ?? '');
  const [bagCount, setBagCount] = useState(String(job?.bag_count ?? 1));
  const bagCountValid = isValidPackagingQuantity(bagCount, 10000);
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isAllBatch = !!job?.all_batch;
  const matchingTemplates = useMemo(() => templates.filter(item => !item.blend_id || item.blend_id === blendId), [templates, blendId]);

  useEffect(() => {
    listPackageTemplates().then(items => {
      setTemplates(items);
      const first = items.find(item => !item.blend_id || item.blend_id === blendId);
      setTemplateId(first?.id ?? '');
      if (!first && !job) setMode('custom');
    }).catch(err => setError(mutationErrorMessage(err)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (mode !== 'template') return;
    const stillMatches = matchingTemplates.some(item => item.id === templateId);
    if (!stillMatches) setTemplateId(matchingTemplates[0]?.id ?? '');
  }, [blendId, mode, matchingTemplates, templateId]);

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!isAllBatch && !bagCountValid) return;
    setSaving(true); setError('');
    try {
      if (job) {
        await updatePackagingJob(job.id, { customer_name: customerName.trim(), blend_id: blendId, bag_type: bagType.trim(), ...(isAllBatch ? { all_batch: true } : { bag_count: Number(bagCount) }) });
      } else {
        await createPackagingJob({
          plan_date: planDate, blend_id: blendId, bag_count: Number(bagCount),
          ...(mode === 'template' ? { template_id: templateId } : { customer_name: customerName.trim(), bag_type: bagType.trim(), save_as_template: saveAsTemplate }),
        });
      }
      onSaved(); onClose();
    } catch (err) { setError(mutationErrorMessage(err)); }
    finally { setSaving(false); }
  };
  const valid = !!blendId && (isAllBatch || bagCountValid) && (job || mode === 'template' ? !!(job || templateId) : !!customerName.trim() && !!bagType.trim());

  return <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
    <div dir="rtl" className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
      <div className="flex justify-between items-center mb-5"><h3 className="text-2xl font-bold">{job ? 'עריכת משימת אריזה' : 'משימת אריזה חדשה'}</h3><button onClick={onClose} className="p-2 text-2xl">✕</button></div>
      <form onSubmit={save} className="space-y-4">
        <div><label className="block font-bold mb-2">תערובת</label><select value={blendId} onChange={e => setBlendId(e.target.value)} required className="w-full px-4 py-3 border-2 rounded-xl bg-white"><option value="">בחר תערובת</option>{blends.map(blend => <option key={blend.id} value={blend.id}>{blend.name}</option>)}</select></div>
        {!job && <div className="grid grid-cols-2 gap-2"><button type="button" onClick={() => setMode('template')} className={`py-2 rounded-xl font-bold ${mode === 'template' ? 'bg-purple-700 text-white' : 'border-2'}`}>הוראה שמורה</button><button type="button" onClick={() => setMode('custom')} className={`py-2 rounded-xl font-bold ${mode === 'custom' ? 'bg-purple-700 text-white' : 'border-2'}`}>הוראה חדשה</button></div>}
        {!job && mode === 'template' ? <div><label className="block font-bold mb-2">הוראה</label><select value={templateId} onChange={e => setTemplateId(e.target.value)} required className="w-full px-4 py-3 border-2 rounded-xl bg-white"><option value="">בחר הוראה</option>{matchingTemplates.map(item => <option key={item.id} value={item.id}>{item.customer_name} — {item.bag_type}</option>)}</select>{!matchingTemplates.length && <p className="text-sm text-orange-700 mt-1">אין הוראות שמורות לתערובת זו.</p>}</div> : <>
          <div><label className="block font-bold mb-2">לקוח</label><input value={customerName} onChange={e => setCustomerName(e.target.value)} required className="w-full px-4 py-3 border-2 rounded-xl" /></div>
          <div><label className="block font-bold mb-2">סוג שקית / הוראות</label><textarea value={bagType} onChange={e => setBagType(e.target.value)} required rows={3} className="w-full px-4 py-3 border-2 rounded-xl" /></div>
          {!job && <label className="flex gap-2 items-center font-bold"><input type="checkbox" checked={saveAsTemplate} onChange={e => setSaveAsTemplate(e.target.checked)} className="w-5 h-5 accent-purple-700" />שמור לשימוש עתידי</label>}
        </>}
        {isAllBatch ? <div className="rounded-xl border-2 border-purple-200 bg-purple-50 px-4 py-3 font-bold text-purple-800">✓ כל האצווה</div> : <div><label className="block font-bold mb-2">מספר שקיות</label><PackagingQuantityCombobox value={bagCount} onChange={setBagCount} max={10000} label="מספר שקיות במשימת אריזה" /></div>}
        {error && <div role="alert" className="text-red-700 font-bold">{error}</div>}
        <div className="grid grid-cols-2 gap-3"><button type="button" onClick={onClose} className="py-3 bg-gray-100 rounded-xl font-bold">ביטול</button><button disabled={saving || !valid} className="py-3 bg-purple-700 text-white rounded-xl font-bold disabled:bg-gray-300">{saving ? 'שומר…' : 'שמור'}</button></div>
      </form>
    </div>
  </div>;
}

function PackagingPanel({ jobs, currentPlanDate, onAdd, onEdit, onDone, onReopen, onInstructions, onDelete, onReorder }: {
  jobs: PackagingJob[];
  currentPlanDate: string;
  onAdd: () => void;
  onEdit: (job: PackagingJob) => void;
  onDone: (job: PackagingJob) => void;
  onReopen: (job: PackagingJob) => void;
  onInstructions: (job: PackagingJob) => void;
  onDelete: (job: PackagingJob) => void;
  onReorder: (status: 'pending' | 'done', ids: string[]) => void;
}) {
  const pending = jobs.filter(job => job.status === 'pending');
  const done = jobs.filter(job => job.status === 'done');
  const [dragged, setDragged] = useState<{ id: string; status: 'pending' | 'done' } | null>(null);
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);
  const cardNodesRef = useRef(new Map<string, HTMLDivElement>());
  const cardPositionsRef = useRef(new Map<string, number>());
  useLayoutEffect(() => {
    const nextPositions = new Map<string, number>();
    cardNodesRef.current.forEach((node, id) => {
      const nextTop = node.getBoundingClientRect().top;
      const previousTop = cardPositionsRef.current.get(id);
      nextPositions.set(id, nextTop);
      if (previousTop !== undefined && previousTop !== nextTop) {
        node.animate(
          [{ transform: `translateY(${previousTop - nextTop}px)` }, { transform: 'translateY(0)' }],
          { duration: 220, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)' },
        );
      }
    });
    cardPositionsRef.current = nextPositions;
  }, [jobs]);
  useEffect(() => {
    if (expandedJobId && !jobs.some(job => job.id === expandedJobId)) {
      setExpandedJobId(null);
    }
  }, [expandedJobId, jobs]);
  const drop = (targetId: string, status: 'pending' | 'done') => {
    if (!dragged || dragged.status !== status || dragged.id === targetId) return;
    const section = (status === 'pending' ? pending : done).filter(item => item.batch_ids.length === 0);
    const oldIndex = section.findIndex(item => item.id === dragged.id);
    const newIndex = section.findIndex(item => item.id === targetId);
    if (oldIndex >= 0 && newIndex >= 0) {
      onReorder(status, arrayMove(section, oldIndex, newIndex).map(item => item.id));
    }
    setDragged(null);
  };
  const card = (job: PackagingJob) => {
    const isBatchCard = job.card_kind === 'batch';
    const isGenerated = isBatchCard || job.card_kind === 'whole_batch' || job.card_kind === 'remainder';
    const title = job.batch_ids.length ? job.blend_name : job.customer_name;
    const quantityLabel = job.card_kind === 'remainder' ? 'יתרת האצווה' : packagingQuantityLabel(job);
    const section = job.status === 'pending' ? pending : done;
    const reorderableSection = section.filter(item => item.batch_ids.length === 0);
    const index = reorderableSection.findIndex(item => item.id === job.id);
    const canReorder = job.batch_ids.length === 0;
    const isExpanded = expandedJobId === job.id;
    const detailsId = `packaging-job-details-${job.id}`;
    const move = (direction: -1 | 1) => {
      const target = index + direction;
      if (!canReorder || target < 0 || target >= reorderableSection.length) return;
      onReorder(job.status, arrayMove(reorderableSection, index, target).map(item => item.id));
    };
    return (
      <div
        key={job.id}
        ref={node => {
          if (node) cardNodesRef.current.set(job.id, node);
          else cardNodesRef.current.delete(job.id);
        }}
        draggable={canReorder}
        onDragStart={() => { if (canReorder) setDragged({ id: job.id, status: job.status }); }}
        onDragEnd={() => setDragged(null)}
        onDragOver={e => { if (canReorder) e.preventDefault(); }}
        onDrop={() => { if (canReorder) drop(job.id, job.status); }}
        className={`rounded-xl border-2 px-2 py-1.5 transition-all duration-200 active:scale-[0.99] ${canReorder ? 'cursor-grab' : ''} ${dragged?.id === job.id ? 'scale-[0.98] opacity-60 shadow-inner' : 'scale-100'} ${job.status === 'pending' ? 'bg-purple-50 border-purple-200' : 'bg-gray-50 border-gray-200 opacity-80'}`}
      >
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setExpandedJobId(current => current === job.id ? null : job.id)}
            aria-expanded={isExpanded}
            aria-controls={detailsId}
            className="flex min-w-0 flex-1 items-center gap-1.5 rounded-lg px-1 py-1 text-right hover:bg-black/5"
          >
            <span className="text-xs text-gray-500 shrink-0" aria-hidden="true">{isExpanded ? '▼' : '◀'}</span>
            <span className="min-w-0 flex-1 whitespace-normal break-words font-bold text-base leading-tight" title={title}>{title}</span>
            {!isBatchCard && <span className="shrink-0 text-sm font-bold text-purple-700">{quantityLabel}</span>}
          </button>
          {job.status === 'pending' && !isExpanded && (
            <button
              type="button"
              onClick={() => onDone(job)}
              className="shrink-0 rounded-lg bg-green-600 px-2 py-1.5 text-xs font-bold text-white hover:bg-green-700"
            >
              ✓ בוצע
            </button>
          )}
          <div className="flex items-center gap-0.5 shrink-0">
            {canReorder && <>
              <button type="button" onClick={() => move(-1)} disabled={index === 0} className="w-7 h-8 bg-white/60 text-gray-700 rounded-lg text-sm font-bold hover:bg-white disabled:opacity-25 disabled:hover:bg-white/60" title="העבר למעלה" aria-label="העבר משימת אריזה למעלה">↑</button>
              <button type="button" onClick={() => move(1)} disabled={index === reorderableSection.length - 1} className="w-7 h-8 bg-white/60 text-gray-700 rounded-lg text-sm font-bold hover:bg-white disabled:opacity-25 disabled:hover:bg-white/60" title="העבר למטה" aria-label="העבר משימת אריזה למטה">↓</button>
            </>}
          </div>
        </div>
        {isBatchCard && job.instructions.length > 0 && (
          <div className="space-y-0.5 px-1 text-xs text-gray-700">
            {job.instructions.map(instruction => (
              <div key={instruction.id} className="whitespace-pre-wrap break-words">
                {instruction.customer_name} · {packagingQuantityLabel(instruction)}
                {instruction.bag_type ? ` — ${instruction.bag_type}` : ''}
              </div>
            ))}
          </div>
        )}
        {!isBatchCard && (
          <div className="px-1 text-xs text-gray-700 whitespace-pre-wrap break-words">
            {job.customer_name}{job.customer_name ? ' · ' : ''}{job.bag_type}
          </div>
        )}
        {job.loading_worker_name && (
          <div className="px-1 mt-0.5 text-xs font-bold text-teal-700">
            👤 העמסה: {job.loading_worker_name}
          </div>
        )}
        {job.batch_ids.length === 1 && <div className="px-1 mt-0.5 text-xs text-gray-500">אצווה <span dir="ltr">{job.batch_ids[0].slice(0, 8)}</span></div>}
        <div id={detailsId} hidden={!isExpanded} className="mt-1.5 border-t border-current/10 pt-1.5">
          <div className="flex items-center justify-between gap-2">
            <div className="text-xs font-semibold text-purple-700">{job.blend_name}</div>
            {canReorder && <span className="text-gray-400 text-base" aria-hidden="true">⠿</span>}
          </div>
          {job.plan_date !== currentPlanDate && (
            <div className="text-xs font-semibold text-orange-700 mt-1">הועבר מתאריך {job.plan_date}</div>
          )}
          {job.batch_ids.length > 0 && <div className="text-xs text-gray-500 mt-1">מקושר ל־{job.batch_ids.length} אצוות</div>}
          <div className="flex gap-1 mt-2 text-sm">
            {!isGenerated && job.status === 'pending' && <button type="button" onClick={() => onEdit(job)} className="p-1.5" title="עריכה" aria-label="עריכת משימת אריזה">✏️</button>}
            {!isGenerated && job.status === 'pending' && <button type="button" onClick={() => onDelete(job)} className="p-1.5" title="מחיקה" aria-label="מחיקת משימת אריזה">🗑</button>}
            {job.batch_ids.length === 1 && job.status === 'pending' && <button type="button" onClick={() => onInstructions(job)} className="px-2 py-1.5 bg-purple-100 rounded-lg text-xs font-bold">הוראות לאצווה</button>}
            {job.status === 'pending' && <button type="button" onClick={() => onDone(job)} className="mr-auto px-2 py-1.5 bg-green-600 text-white rounded-lg text-xs font-bold">✓ בוצע</button>}
            {job.status === 'done' && <button type="button" onClick={() => onReopen(job)} className="mr-auto px-2 py-1.5 bg-purple-100 rounded-lg text-xs font-bold">פתח מחדש</button>}
          </div>
        </div>
      </div>
    );
  };
  return <div dir="rtl" className="bg-white rounded-2xl shadow-lg p-4 2xl:p-5 flex flex-col overflow-hidden">
    <div className="flex items-center justify-between gap-2 mb-2 shrink-0"><h2 className="text-base 2xl:text-lg font-bold text-gray-700">אריזה</h2><button onClick={onAdd} className="px-2 py-1.5 bg-purple-700 text-white rounded-lg text-sm font-bold whitespace-nowrap">+ הוסף</button></div>
    <div className="overflow-y-auto flex-1 min-h-0 space-y-1.5">
      <div className="text-sm font-bold text-purple-700">לביצוע ({pending.length})</div>{pending.map(card)}{!pending.length && <p className="text-center text-gray-400 py-3">אין משימות אריזה פתוחות.</p>}
      <div className="text-sm font-bold text-gray-500 pt-3 border-t">הושלמו ({done.length})</div>{done.map(card)}
    </div>
  </div>;
}

// ── Loading Confirmation Modal ─────────────────────────────────────

function LoadingConfirmationModal({
  batch,
  mid,
  onLoaded,
  onCancel,
}: {
  batch: RoastBatch;
  mid: string;
  onLoaded: (workerName: string) => void;
  onCancel: () => void;
}) {
  const [workerName, setWorkerName] = useState(batch.loading_worker_name ?? '');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onCancel}>
      <div
        dir="rtl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="loading-confirmation-title"
        className="max-h-[calc(100dvh-2rem)] w-full max-w-7xl overflow-y-auto rounded-2xl bg-white p-4 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <h3 id="loading-confirmation-title" className="text-center text-2xl font-extrabold text-gray-800">
          טעינת {batch.blend_name} לתנור {mid}
        </h3>
        <p className="mt-1 text-center text-gray-500">לאחר סיום ההעמסה, אשרו מי העמיס את הקפה הירוק.</p>

        <div className="mt-3 grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
          <BlendRecipeDisplay recipe={batch.blend_recipe} prominent />

          <div className="min-w-0 rounded-xl border border-gray-200 bg-gray-50 p-3">
            <h4 className="mb-3 text-lg font-bold text-gray-800">מי העמיס את הקפה?</h4>
            <LoadingWorkerNamePicker
              idPrefix={`loading-confirmation-${batch.id}`}
              value={workerName}
              onChange={setWorkerName}
              allowEmpty={false}
            />

            <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-1">
              <button type="button" onClick={onCancel} className="rounded-xl bg-gray-100 py-3 text-lg font-bold text-gray-600 hover:bg-gray-200 lg:order-last">
                ביטול
              </button>
              <button
                type="button"
                disabled={!workerName.trim()}
                onClick={() => onLoaded(workerName.trim())}
                className="rounded-xl bg-green-600 py-3 text-lg font-bold text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-gray-300"
              >
                אישור סיום העמסה
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main Manager Screen ──────────────────────────────────────────────

// Sensor readings are considered stale (i.e. the Modbus collector is no longer
// receiving data from the PLC — most likely an unplugged Modbus/Ethernet cable)
// once the freshest reading is older than this threshold. The collector polls
// every 5s, so 90s leaves ample margin to avoid flapping.
const SENSOR_STALE_MS = 90_000;

// A vision device uploads an event roughly every 15s while its camera is
// working (idle snapshots + monitoring frames). Once the freshest event for an
// oven-assigned camera is older than this, the camera is considered
// disconnected / not streaming. 60s ≈ 4 missed snapshots — enough to avoid
// flapping while still surfacing quickly.
const CAMERA_STALE_MS = 60_000;

function formatSensorAge(ms: number): string {
  const secs = Math.floor(ms / 1000);
  if (secs < 60) return `לפני ${secs} שנ׳`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `לפני ${mins} דק׳`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `לפני ${hrs} שע׳`;
  const days = Math.floor(hrs / 24);
  return `לפני ${days} ימים`;
}

function Manager() {
  const { todayBatches, strayOccupants: todayStrayOccupants, ovenQueues: todayOvenQueues, blends: sharedBlends, allSensorReadings, refreshPlan, refreshBlends, updateBlends } = useAppData();
  const [blends, setBlends] = useState<Blend[]>(sharedBlends);
  const blendDragRef = useRef(false);
  const [isBlendDragging, setIsBlendDragging] = useState(false);
  const [batches, setBatches] = useState<RoastBatch[]>([]);
  const [strayOccupants, setStrayOccupants] = useState<RoastBatch[]>([]);
  const [ovenQueues, setOvenQueues] = useState<OvenQueues>({});
  const [planDate, setPlanDate] = useState(todayStr());
  const planDateRef = useRef(planDate);
  planDateRef.current = planDate;
  const [selectedBlend, setSelectedBlend] = useState<Blend | null>(null);
  const [showBlendForm, setShowBlendForm] = useState(false);
  const [editingBlend, setEditingBlend] = useState<Blend | null>(null);
  const [showBlendManagement, setShowBlendManagement] = useState(false);
  const [isPlanFocused, setIsPlanFocused] = useState(false);
  const [showOven2, setShowOven2] = useState(initialOven2Visibility);
  const [showPackageManagement, setShowPackageManagement] = useState(false);
  const [packageBatches, setPackageBatches] = useState<RoastBatch[] | null>(null);
  const [packagingJobs, setPackagingJobs] = useState<PackagingJob[]>([]);
  const [editingPackagingJob, setEditingPackagingJob] = useState<PackagingJob | null>(null);
  const [showPackagingJobForm, setShowPackagingJobForm] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [expandedPlannedBatchId, setExpandedPlannedBatchId] = useState<string | null>(null);
  const [collapsedFocusedPlanBatchIds, setCollapsedFocusedPlanBatchIds] = useState<Set<string>>(() => new Set());
  const [expandedOvenBatchId, setExpandedOvenBatchId] = useState<string | null>(null);
  const [visionByOven, setVisionByOven] = useState<Record<string, string | null>>({});
  const [visionStatusByDevice, setVisionStatusByDevice] = useState<Record<string, VisionDeviceStatus>>({});
  const [openConnectionError, setOpenConnectionError] = useState<'modbus' | 'camera' | null>(null);
  const [nowMs, setNowMs] = useState(Date.now());
  const [loadingConfirmation, setLoadingConfirmation] = useState<{ batch: RoastBatch; mid: string } | null>(null);
  const [saveState, setSaveState] = useState<{
    status: 'idle' | 'saving' | 'saved' | 'error';
    message: string;
  }>({ status: 'idle', message: '' });
  const mutationInFlightRef = useRef(false);
  const reorderQueuesRef = useRef(new Map<string, {
    tail: Promise<void>;
    latestOperation: number;
    pending: number;
    restoreCommitted: () => void;
  }>());
  const reorderOperationRef = useRef(0);
  const pendingReorderCountRef = useRef(0);
  const reorderFailureRef = useRef(false);
  const [pendingReorderCount, setPendingReorderCount] = useState(0);
  useEffect(() => {
    if (!blendDragRef.current && !reorderQueuesRef.current.has('blends')) {
      setBlends(sharedBlends);
    }
  }, [sharedBlends]);
  const currentDate = todayStr();
  const previousCurrentDateRef = useRef(currentDate);

  useEffect(() => {
    try {
      localStorage.setItem(OVEN_2_VISIBILITY_KEY, String(showOven2));
    } catch {
      // The toggle still works for this page when browser storage is unavailable.
    }
  }, [showOven2]);

  const isToday = planDate === currentDate;
  const sensorReadings = allSensorReadings;
  const sensorsLoaded = allSensorReadings.length > 0;

  useEffect(() => {
    const previousDate = previousCurrentDateRef.current;
    if (currentDate === previousDate) return;
    setPlanDate(selectedDate => selectedDate === previousDate ? currentDate : selectedDate);
    previousCurrentDateRef.current = currentDate;
  }, [currentDate]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
  );

  const runMutation = useCallback(async (
    action: () => Promise<unknown>,
    successMessage: string,
    onError?: (error: unknown) => void | Promise<void>,
  ): Promise<boolean> => {
    if (mutationInFlightRef.current) return false;
    mutationInFlightRef.current = true;
    setSaveState({ status: 'saving', message: 'שומר שינויים…' });
    try {
      await action();
      setSaveState({ status: 'saved', message: successMessage });
      return true;
    } catch (error) {
      console.error(`Mutation failed (expected: "${successMessage}")`, error);
      setSaveState({ status: 'error', message: mutationErrorMessage(error) });
      await onError?.(error);
      return false;
    } finally {
      mutationInFlightRef.current = false;
    }
  }, []);

  // For historical dates: self-fetch plan; for today: sync from shared context.
  const loadHistoricalPlan = useCallback(async () => {
    if (planDate === todayStr()) return;
    try {
      const data = await getDailyPlan(planDate);
      if (planDateRef.current !== planDate) return;
      if (reorderQueuesRef.current.has(`batches:${planDate}`)) return;
      setBatches(prev =>
        JSON.stringify(prev) === JSON.stringify(data.batches) ? prev : data.batches);
      setStrayOccupants(prev =>
        JSON.stringify(prev) === JSON.stringify(data.stray_occupants) ? prev : data.stray_occupants);
      setOvenQueues(data.oven_queues);
    } catch (err) {
      console.error('Failed to load plan:', err);
      setSaveState({ status: 'error', message: mutationErrorMessage(err) });
    }
  }, [planDate]);

  useEffect(() => { loadHistoricalPlan(); }, [loadHistoricalPlan]);

  const loadPackagingJobs = useCallback(async () => {
    try {
      const jobs = await listPackagingJobs(planDate, isToday);
      if (planDateRef.current !== planDate) return;
      const hasPendingReorder = Array.from(reorderQueuesRef.current.keys()).some(key =>
        key.startsWith(`packaging:${planDate}:`)
        || (isToday && key === 'packaging:carry-over:pending'),
      );
      if (!hasPendingReorder) {
        setPackagingJobs(jobs);
      }
    }
    catch (err) { console.error('Failed to load packaging jobs:', err); }
  }, [isToday, planDate]);
  useEffect(() => { loadPackagingJobs(); }, [loadPackagingJobs]);
  useEffect(() => {
    if (isToday) loadPackagingJobs();
  }, [isToday, loadPackagingJobs, todayBatches]);

  // ── Cross-screen sync: pause context sync while the user is mid-interaction. ──
  const isBusyRef = useRef(false);
  useEffect(() => {
    isBusyRef.current = pendingReorderCount > 0 || isBlendDragging || isDragOver || !!selectedBlend || showBlendForm || showPackageManagement || !!packageBatches || showPackagingJobForm;
  }, [pendingReorderCount, isBlendDragging, isDragOver, selectedBlend, showBlendForm, showPackageManagement, packageBatches, showPackagingJobForm]);

  // Sync from context when showing today — skip during drag to avoid visual jumps.
  useEffect(() => {
    if (!isToday) return;
    if (isBusyRef.current) return;
    setBatches(prev =>
      JSON.stringify(prev) === JSON.stringify(todayBatches) ? prev : todayBatches,
    );
    setStrayOccupants(prev =>
      JSON.stringify(prev) === JSON.stringify(todayStrayOccupants) ? prev : todayStrayOccupants,
    );
    setOvenQueues(todayOvenQueues);
  }, [todayBatches, todayStrayOccupants, todayOvenQueues, isToday]);

  // After a mutation: refresh shared plan (propagates to OvenStatus, Operator, Dashboard)
  // or re-fetch historical plan if viewing a past date.
  const reloadPlan = useCallback(() => {
    return isToday ? refreshPlan() : loadHistoricalPlan();
  }, [isToday, refreshPlan, loadHistoricalPlan]);

  const enqueueReorderSave = useCallback((
    queueKey: string,
    persist: () => Promise<unknown>,
    restoreBefore: () => void,
    restoreAfter: () => void,
    successMessage: string,
    onQueueDrained?: () => void,
  ) => {
    const operationId = ++reorderOperationRef.current;
    let queue = reorderQueuesRef.current.get(queueKey);
    if (!queue) {
      queue = {
        tail: Promise.resolve(),
        latestOperation: operationId,
        pending: 0,
        restoreCommitted: restoreBefore,
      };
      reorderQueuesRef.current.set(queueKey, queue);
    } else {
      queue.latestOperation = operationId;
    }
    queue.pending += 1;

    if (pendingReorderCountRef.current === 0) reorderFailureRef.current = false;
    pendingReorderCountRef.current += 1;
    setPendingReorderCount(pendingReorderCountRef.current);
    isBusyRef.current = true;
    setSaveState({ status: 'saving', message: 'שומר סדר ברקע…' });

    const currentQueue = queue;
    currentQueue.tail = currentQueue.tail.then(async () => {
      try {
        await persist();
        currentQueue.restoreCommitted = restoreAfter;
      } catch (error) {
        console.error(`Reorder failed (expected: "${successMessage}")`, error);
        reorderFailureRef.current = true;
        if (currentQueue.latestOperation === operationId) currentQueue.restoreCommitted();
        setSaveState({ status: 'error', message: mutationErrorMessage(error) });
      } finally {
        currentQueue.pending -= 1;
        if (currentQueue.pending === 0 && reorderQueuesRef.current.get(queueKey) === currentQueue) {
          reorderQueuesRef.current.delete(queueKey);
          onQueueDrained?.();
        }
        pendingReorderCountRef.current -= 1;
        setPendingReorderCount(pendingReorderCountRef.current);
        if (pendingReorderCountRef.current === 0) {
          isBusyRef.current = false;
          if (!reorderFailureRef.current) {
            setSaveState({ status: 'saved', message: successMessage });
          }
        }
      }
    });
  }, []);

  const handleBlendDragStart = () => {
    blendDragRef.current = true;
    setIsBlendDragging(true);
  };

  const handleBlendDragCancel = () => {
    blendDragRef.current = false;
    setIsBlendDragging(false);
    if (!reorderQueuesRef.current.has('blends')) setBlends(sharedBlends);
  };

  const handleBlendDragEnd = (event: DragEndEvent) => {
    blendDragRef.current = false;
    setIsBlendDragging(false);
    const { active, over } = event;
    if (!over || active.id === over.id) {
      if (!reorderQueuesRef.current.has('blends')) setBlends(sharedBlends);
      return;
    }
    const oldIndex = blends.findIndex(blend => blend.id === active.id);
    const newIndex = blends.findIndex(blend => blend.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    if (blends.length > 100) {
      setSaveState({ status: 'error', message: 'ניתן לשנות סדר של עד 100 תערובות.' });
      return;
    }
    const before = blends;
    const reordered = arrayMove(blends, oldIndex, newIndex);
    let committed = before;
    setBlends(reordered);
    enqueueReorderSave(
      'blends',
      async () => {
        committed = await reorderBlends(reordered.map(blend => blend.id));
        updateBlends(committed);
      },
      () => setBlends(before),
      () => setBlends(committed),
      'סדר התערובות נשמר',
      () => {
        void refreshBlends().then(data => {
          if (data && !blendDragRef.current && !reorderQueuesRef.current.has('blends')) {
            setBlends(data);
          }
        });
      },
    );
  };

  useEffect(() => {
    const id = setInterval(() => { if (!document.hidden) setNowMs(Date.now()); }, 5000);
    return () => clearInterval(id);
  }, []);

  // Freshest reading timestamp across all devices. Only treat as "unplugged"
  // once data has actually flowed at least once but has since gone stale.
  const latestSensorMs = sensorReadings.reduce((max, r) => {
    const t = new Date(r.time).getTime();
    return Number.isFinite(t) && t > max ? t : max;
  }, 0);
  const sensorAgeMs = latestSensorMs > 0 ? nowMs - latestSensorMs : null;
  const sensorsStale =
    sensorsLoaded && latestSensorMs > 0 && sensorAgeMs !== null && sensorAgeMs > SENSOR_STALE_MS;

  // ── Camera connectivity: detect when an oven-assigned vision device stops
  //    uploading events (e.g. the USB camera is unplugged). Resolve each oven's
  //    vision device once, then poll its status and flag stale ones. ──
  useEffect(() => {
    OVEN_IDS.forEach(async (mid) => {
      try {
        const config = await getOvenConfig(mid);
        setVisionByOven(prev => ({ ...prev, [mid]: config.vision_device_id ?? null }));
      } catch {
        setVisionByOven(prev => ({ ...prev, [mid]: null }));
      }
    });
  }, []);

  const loadVisionStatuses = useCallback(async () => {
    const deviceIds = Array.from(
      new Set(Object.values(visionByOven).filter((id): id is string => !!id)),
    );
    await Promise.all(deviceIds.map(async (deviceId) => {
      try {
        const status = await getVisionDeviceStatus(deviceId);
        setVisionStatusByDevice(prev => ({ ...prev, [deviceId]: status }));
      } catch { /* network error — keep previous status, don't false-alarm */ }
    }));
  }, [visionByOven]);

  useEffect(() => { loadVisionStatuses(); }, [loadVisionStatuses]);

  useEffect(() => {
    const tick = () => { if (!document.hidden) loadVisionStatuses(); };
    const id = setInterval(tick, 15000);
    return () => clearInterval(id);
  }, [loadVisionStatuses]);

  // Ovens whose assigned camera has reported at least once but has since gone
  // stale. Skip devices with a null last_event_at (never provisioned / never
  // streamed) to avoid false alarms.
  const disconnectedCameraOvens = OVEN_IDS.filter((mid) => {
    const deviceId = visionByOven[mid];
    if (!deviceId) return false;
    const status = visionStatusByDevice[deviceId];
    if (!status || !status.last_event_at) return false;
    const t = new Date(status.last_event_at).getTime();
    if (!Number.isFinite(t)) return false;
    return nowMs - t > CAMERA_STALE_MS;
  });

  useEffect(() => {
    if (
      (openConnectionError === 'modbus' && !sensorsStale)
      || (openConnectionError === 'camera' && disconnectedCameraOvens.length === 0)
    ) {
      setOpenConnectionError(null);
    }
  }, [openConnectionError, sensorsStale, disconnectedCameraOvens.length]);

  // ── Blend CRUD ──
  const handleSaveBlend = async (name: string, totalSeconds: number, targetColorRgb: [number, number, number] | null, targetRoastScore: number | null, recipe: BlendRecipe | null) => {
    const wasEditing = !!editingBlend;
    const success = await runMutation(async () => {
      if (editingBlend) {
        await updateBlend(editingBlend.id, { name, required_roast_time_seconds: totalSeconds, target_color_rgb: targetColorRgb, target_roast_score: targetRoastScore, recipe });
        setEditingBlend(null);
      } else {
        await createBlend(name, totalSeconds, targetColorRgb, targetRoastScore, undefined, undefined, recipe);
      }
    }, wasEditing ? 'התערובת עודכנה ונשמרה' : 'התערובת נוספה ונשמרה');
    if (success) {
      setShowBlendForm(false);
      refreshBlends();
    }
  };

  const handleDeleteBlend = async (id: string) => {
    if (!confirmWithoutLeavingFullscreen('למחוק תערובת זו?')) return;
    if (await runMutation(() => deleteBlend(id), 'התערובת נמחקה')) {
      refreshBlends();
    }
  };

  // ── Batch CRUD ──
  const handleAddBatch = async (blendId: string, quantity: number, unit: string, batchWeightKg: number, roastTimeSeconds: number, loadingWorkerName?: string, packaging?: PackagingInstructionInput) => {
    let created: RoastBatch[] = [];
    if (await runMutation(
      async () => { created = await createBatch(blendId, quantity, unit, planDate, batchWeightKg, roastTimeSeconds, loadingWorkerName, packaging); },
      'התכנית נשמרה',
    )) {
      setSelectedBlend(null);
      setBatches(prev => [...prev, ...created]);
      reloadPlan();
      loadPackagingJobs();
    }
  };

  const handleUpdateLoaderName = async (batch: RoastBatch, loadingWorkerName: string | null): Promise<boolean> => {
    let updated: RoastBatch | null = null;
    const success = await runMutation(
      async () => {
        updated = await updateBatch(batch.id, { loading_worker_name: loadingWorkerName });
      },
      loadingWorkerName ? 'שם המעמיס נשמר' : 'שם המעמיס הוסר',
    );
    if (success && updated) {
      setBatches(current => current.map(item => item.id === updated!.id ? updated! : item));
    }
    return success;
  };

  const handleUpdateRoastTime = async (batch: RoastBatch, roastTimeSeconds: number): Promise<boolean> => {
    let updated: RoastBatch | null = null;
    const success = await runMutation(
      async () => {
        updated = await updateBatch(batch.id, { planned_roast_time_seconds: roastTimeSeconds });
      },
      'זמן הקלייה נשמר',
    );
    if (success && updated) {
      setBatches(current => current.map(item => item.id === updated!.id ? updated! : item));
    }
    return success;
  };

  const handlePackagingDone = async (job: PackagingJob) => {
    if (await runMutation(() => completePackagingJob(job.id), 'האריזה סומנה כבוצעה')) {
      await loadPackagingJobs(); reloadPlan();
    }
  };
  const handlePackagingDelete = async (job: PackagingJob) => {
    if (!confirmWithoutLeavingFullscreen(`למחוק את משימת האריזה עבור ${job.batch_ids.length ? job.blend_name : job.customer_name}?`)) return;
    if (await runMutation(() => deletePackagingJob(job.id), 'משימת האריזה נמחקה')) {
      await loadPackagingJobs(); reloadPlan();
    }
  };
  const handlePackagingReopen = async (job: PackagingJob) => {
    if (await runMutation(() => reopenPackagingJob(job.id), 'משימת האריזה נפתחה מחדש')) {
      await loadPackagingJobs(); reloadPlan();
    }
  };
  const handlePackagingInstructions = async (job: PackagingJob) => {
    await runMutation(async () => {
      const batch = await getBatch(job.batch_ids[0]);
      setPackageBatches([batch]);
    }, 'הוראות האצווה נטענו');
  };
  const handlePackagingReorder = (status: 'pending' | 'done', ids: string[]) => {
    const previousIds = packagingJobs
      .filter(job => job.status === status && job.batch_ids.length === 0)
      .map(job => job.id);
    const persist = isToday && status === 'pending'
      ? () => reorderCarryOverPackagingJobs(planDate, ids)
      : () => reorderPackagingJobs(planDate, status, ids);
    setPackagingJobs(current => applyPackagingOrder(current, status, ids));
    enqueueReorderSave(
      isToday && status === 'pending' ? 'packaging:carry-over:pending' : `packaging:${planDate}:${status}`,
      persist,
      () => setPackagingJobs(current => applyPackagingOrder(current, status, previousIds)),
      () => setPackagingJobs(current => applyPackagingOrder(current, status, ids)),
      'סדר האריזות נשמר',
      loadPackagingJobs,
    );
  };

  const handlePackageBatchUpdated = (updated: RoastBatch) => {
    setBatches(prev => prev.map(batch => batch.id === updated.id ? updated : batch));
    setPackageBatches(prev => prev?.map(batch => batch.id === updated.id ? updated : batch) ?? null);
    loadPackagingJobs();
  };

  const handleDeleteBatch = async (id: string) => {
    if (!confirmWithoutLeavingFullscreen('למחוק את האצווה מהתכנית? פעולה זו אינה ניתנת לביטול.')) return;
    if (await runMutation(() => deleteBatch(id), 'האצווה נמחקה מהתכנית')) {
      reloadPlan();
      loadPackagingJobs();
    }
  };

  const handleCancelBatch = async (id: string) => {
    if (!confirmWithoutLeavingFullscreen('לבטל את הקלייה הפעילה ולהחזיר את האצווה לתכנית?')) return;
    if (await runMutation(() => cancelBatch(id), 'הקלייה בוטלה והתכנית נשמרה')) {
      reloadPlan();
    }
  };

  // ── Derived batch lists ──
  // Loaded waiting batches stay in the plan; loading FIFO is independent of row order.
  const plannedBatches = batches
    .filter((b) => b.status === 'planned')
    .sort((a, b) => a.sort_order - b.sort_order);
  // Everything tied to a specific oven (assigned/planned, in-progress, or done).
  const ovenBatches = (mid: string) => batches.filter((b) => b.machine_id === mid);

  useEffect(() => {
    if (
      expandedPlannedBatchId
      && !batches.some(
        (batch) => batch.id === expandedPlannedBatchId
          && batch.status === 'planned',
      )
    ) {
      setExpandedPlannedBatchId(null);
    }
    if (
      expandedOvenBatchId
      && !batches.some(
        (batch) => batch.id === expandedOvenBatchId
          && !!batch.machine_id
          && batch.status !== 'in-progress',
      )
    ) {
      setExpandedOvenBatchId(null);
    }
  }, [batches, expandedOvenBatchId, expandedPlannedBatchId]);

  // ── Oven assignment & roast controls ──
  const handleAssignToOven = (batchId: string, mid: string) => {
    const batch = batches.find((item) => item.id === batchId);
    if (!batch || batch.machine_id || batch.status !== 'planned') return;
    if ((ovenQueues[mid]?.pending_batch_ids.length ?? 0) >= 2) {
      setSaveState({ status: 'error', message: `תנור ${mid} מלא: שתי אצוות שהועמסו כבר ממתינות לכניסה לתנור` });
      return;
    }
    setLoadingConfirmation({ batch, mid });
  };

  const confirmLoadedIntoOven = async (batchId: string, mid: string, loadingWorkerName: string) => {
    setLoadingConfirmation(null);
    if (await runMutation(
      () => assignBatch(batchId, mid, loadingWorkerName),
      `ההעמסה הסתיימה והאצווה ממתינה לכניסה לתנור ${mid}`,
      async (error) => {
        if (mutationStatus(error) === 409) await reloadPlan();
      },
    )) {
      setExpandedPlannedBatchId(batchId);
      await reloadPlan();
    }
  };

  const handleUnassign = async (id: string) => {
    if (await runMutation(() => unassignBatch(id), 'שיוך האצווה לתנור בוטל', async () => { await reloadPlan(); })) {
      await reloadPlan();
    }
  };

  const handleStart = async (batchId: string, mid: string) => {
    // One active roast per oven (mirrors the operator screen).
    if ([...ovenBatches(mid), ...strayOccupants.filter(b => b.machine_id === mid)].some((b) => b.status === 'in-progress')) {
      alertWithoutLeavingFullscreen(`תנור ${mid} תפוס כרגע`);
      return;
    }
    if (ovenQueues[mid]?.pending_batch_ids[0] !== batchId) {
      setSaveState({ status: 'error', message: 'רק האצווה הראשונה שהועמסה יכולה להיכנס לתנור' });
      return;
    }
    if (await runMutation(() => startBatch(batchId, mid), `הקלייה בתנור ${mid} התחילה`, async () => { await reloadPlan(); })) {
      await reloadPlan();
    }
  };

  const handleStartTimer = async (id: string) => {
    if (await runMutation(() => startTimer(id), 'הטיימר התחיל ונשמר')) {
      reloadPlan();
    }
  };

  const handleFinish = async (id: string) => {
    const batch = batches.find(item => item.id === id) ?? strayOccupants.find(item => item.id === id);
    const remaining = batch ? liveRemaining(batch, Date.now()) : null;
    if ((!batch?.timer_running || (remaining !== null && remaining > 0)) &&
        !confirmWithoutLeavingFullscreen('הטיימר עדיין לא הסתיים. לסמן את הקלייה כהושלמה בכל זאת?')) return;
    if (await runMutation(() => finishBatch(id), 'הקלייה הושלמה ונשמרה')) {
      setExpandedOvenBatchId(current => current === id ? null : current);
      reloadPlan();
    }
  };

  // ── dnd-kit: reorder planned batches, or assign one to an oven ──
  // isBusyRef stays set (from onDragStart) until the persist resolves so a poll
  // can't overwrite the optimistic state mid-flight.
  const handleDragEnd = async (event: DragEndEvent) => {
    try {
      const { active, over } = event;
      if (!over) return;

      // Dropped onto an oven panel → assign the planned batch to that oven.
      if (typeof over.id === 'string' && over.id.startsWith('oven-')) {
        const mid = over.id.slice('oven-'.length);
        if (plannedBatches.some((b) => b.id === active.id && !b.machine_id)) {
          await handleAssignToOven(String(active.id), mid);
        }
        return;
      }

      if (active.id === over.id) return;

      const oldIndex = plannedBatches.findIndex((b) => b.id === active.id);
      const newIndex = plannedBatches.findIndex((b) => b.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;
      const previousIds = plannedBatches.map(batch => batch.id);
      const reorderedPlanned = arrayMove(plannedBatches, oldIndex, newIndex);
      const reorderedIds = reorderedPlanned.map(batch => batch.id);
      setBatches(current => applyPlannedBatchOrder(current, reorderedIds));
      enqueueReorderSave(
        `batches:${planDate}`,
        () => reorderBatches(
          reorderedIds,
        ),
        () => setBatches(current => applyPlannedBatchOrder(current, previousIds)),
        () => setBatches(current => applyPlannedBatchOrder(current, reorderedIds)),
        'סדר התכנית נשמר',
        () => {
          reloadPlan();
          void loadPackagingJobs();
        },
      );
    } finally {
      isBusyRef.current = pendingReorderCountRef.current > 0;
    }
  };

  const handleMovePlannedBatch = (batchId: string, direction: -1 | 1) => {
    const oldIndex = plannedBatches.findIndex((batch) => batch.id === batchId);
    const newIndex = oldIndex + direction;
    if (oldIndex < 0 || newIndex < 0 || newIndex >= plannedBatches.length) return;
    isBusyRef.current = true;
    const previousIds = plannedBatches.map(batch => batch.id);
    const reorderedPlanned = arrayMove(plannedBatches, oldIndex, newIndex);
    const reorderedIds = reorderedPlanned.map(batch => batch.id);
    setBatches(current => applyPlannedBatchOrder(current, reorderedIds));
    enqueueReorderSave(
      `batches:${planDate}`,
      () => reorderBatches(reorderedIds),
      () => setBatches(current => applyPlannedBatchOrder(current, previousIds)),
      () => setBatches(current => applyPlannedBatchOrder(current, reorderedIds)),
      'סדר התכנית נשמר',
      () => {
        reloadPlan();
        void loadPackagingJobs();
      },
    );
  };

  // ── HTML5 native drag: blend → plan drop zone ──
  const handlePlanDragOver = (e: React.DragEvent) => {
    if (e.dataTransfer.types.includes('application/blend-id')) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      setIsDragOver(true);
    }
  };

  const handlePlanDragLeave = (e: React.DragEvent) => {
    // Only clear if leaving the drop zone itself (not entering a child)
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setIsDragOver(false);
  };

  const handlePlanDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const blendId = e.dataTransfer.getData('application/blend-id');
    if (!blendId) return;
    const blend = blends.find((b) => b.id === blendId);
    if (blend) {
      setSelectedBlend(blend);
    }
  };

  return (
    <div className="flex flex-col h-full min-h-0 gap-3">
      <div className="flex items-center justify-between shrink-0">
        <h1 className="text-xl font-extrabold text-gray-800">מנהל — תכנון ייצור</h1>
        <div className="flex items-center gap-2">
          <div
            role={saveState.status === 'error' ? 'alert' : 'status'}
            aria-live="polite"
            aria-label={saveState.message || 'מצב שמירה'}
            title={saveState.message || 'מצב שמירה'}
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 text-lg font-bold shadow-sm transition-opacity ${
              saveState.status === 'saving'
                ? 'bg-blue-50 border-blue-400 text-blue-800'
                : saveState.status === 'saved'
                  ? 'bg-green-50 border-green-400 text-green-800'
                  : saveState.status === 'error'
                    ? 'bg-red-50 border-red-400 text-red-800'
                    : 'border-transparent opacity-0'
            }`}
          >
            <span className={saveState.status === 'saving' ? 'animate-spin' : ''} aria-hidden="true">
              {saveState.status === 'saving' ? '↻' : saveState.status === 'saved' ? '✓' : '!'}
            </span>
          </div>
          {sensorsStale && (
            <button
              type="button"
              onClick={() => setOpenConnectionError(openConnectionError === 'modbus' ? null : 'modbus')}
              title="שגיאת חיבור Modbus"
              aria-label="הצג שגיאת חיבור Modbus"
              aria-expanded={openConnectionError === 'modbus'}
              className="w-12 h-12 shrink-0 rounded-xl border-2 border-red-400 bg-red-50 text-2xl shadow-sm hover:bg-red-100 transition-colors animate-pulse"
            >
              🔌
            </button>
          )}
          {disconnectedCameraOvens.length > 0 && (
            <button
              type="button"
              onClick={() => setOpenConnectionError(openConnectionError === 'camera' ? null : 'camera')}
              title="שגיאת חיבור מצלמה"
              aria-label="הצג שגיאת חיבור מצלמה"
              aria-expanded={openConnectionError === 'camera'}
              className="w-12 h-12 shrink-0 rounded-xl border-2 border-orange-400 bg-orange-50 text-2xl shadow-sm hover:bg-orange-100 transition-colors animate-pulse"
            >
              📷
            </button>
          )}
          <button onClick={() => setShowPackageManagement(true)} className="px-3 py-1.5 bg-purple-100 text-purple-800 rounded-lg text-sm font-bold hover:bg-purple-200 transition-colors"><span className="text-xs" aria-hidden="true">🛍</span> ניהול אריזות</button>
          <button
            type="button"
            onClick={() => setShowOven2(visible => !visible)}
            aria-pressed={!showOven2}
            className="px-3 py-1.5 bg-blue-100 text-blue-800 rounded-lg text-sm font-bold hover:bg-blue-200 transition-colors"
          >
            {showOven2 ? 'הסתר תנור 2' : 'הצג תנור 2'}
          </button>
          <button
            onClick={() => setShowBlendManagement(!showBlendManagement)}
            className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg text-sm font-bold hover:bg-gray-300 transition-colors"
          >
            {showBlendManagement ? 'סגור ניהול תערובות' : <><span className="text-xs" aria-hidden="true">⚙</span> ניהול תערובות</>}
          </button>
        </div>
      </div>

      {saveState.status === 'error' && (
        <p className="shrink-0 rounded-xl border border-red-300 bg-red-50 px-4 py-2 text-sm font-bold text-red-800">
          {saveState.message}
        </p>
      )}

      {openConnectionError && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setOpenConnectionError(null)}
        >
          <div
            dir="rtl"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="connection-error-title"
            className={`w-full max-w-lg rounded-2xl border-2 p-6 shadow-2xl ${
              openConnectionError === 'modbus'
                ? 'border-red-300 bg-red-50'
                : 'border-orange-300 bg-orange-50'
            }`}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start gap-4">
              <span className="text-4xl" aria-hidden="true">
                {openConnectionError === 'modbus' ? '🔌' : '📷'}
              </span>
              <div className="flex-1">
                <h2
                  id="connection-error-title"
                  className={`text-xl font-bold ${
                    openConnectionError === 'modbus' ? 'text-red-700' : 'text-orange-700'
                  }`}
                >
                  {openConnectionError === 'modbus'
                    ? 'אין נתוני חיישנים — ייתכן שכבל ה-Modbus מנותק'
                    : disconnectedCameraOvens.length === 1
                      ? `מצלמת תנור ${disconnectedCameraOvens[0]} מנותקת — אין שידור וידאו`
                      : `מצלמות התנורים ${disconnectedCameraOvens.join(', ')} מנותקות — אין שידור וידאו`}
                </h2>
                <p className={`mt-2 text-base ${
                  openConnectionError === 'modbus' ? 'text-red-600' : 'text-orange-600'
                }`}>
                  {openConnectionError === 'modbus'
                    ? `נתונים אחרונים התקבלו ${sensorAgeMs !== null ? formatSensorAge(sensorAgeMs) : ''}. בדקו את חיבור ה-Modbus/Ethernet של אוסף הנתונים.`
                    : `בדקו את חיבור המצלמה של ${disconnectedCameraOvens.length === 1 ? 'התנור' : 'התנורים'}.`}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOpenConnectionError(null)}
                aria-label="סגור"
                className="text-2xl text-gray-500 hover:text-gray-800"
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}

      <StrayOccupantBanner
        batches={strayOccupants}
        onFinish={handleFinish}
        onCancel={handleCancelBatch}
        onUnassign={handleUnassign}
      />

      {/* ── Blend Management (collapsible) ── */}
      {showBlendManagement && (        <div className="bg-white rounded-2xl shadow-lg p-6 shrink-0">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-700">ניהול תערובות</h2>
            <button
              onClick={() => { setEditingBlend(null); setShowBlendForm(true); }}
              className="px-5 py-3 bg-amber-700 text-white rounded-xl text-lg font-bold hover:bg-amber-800 transition-colors"
            >
              + תערובת חדשה
            </button>
          </div>
          <p className="text-sm text-gray-500 mb-3">
            {blends.length > 100 ? 'ניתן לשנות סדר של עד 100 תערובות.' : 'גרור את ידית התערובת לשינוי הסדר. הסדר נשמר לכל המשתמשים.'}
          </p>
          <SortableBlendGrid
            blends={blends}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3"
            onDragStart={handleBlendDragStart}
            onDragEnd={handleBlendDragEnd}
            onDragCancel={handleBlendDragCancel}
          >
            {blend => (
              <div className="bg-gray-50 rounded-xl p-4 pt-8 h-full flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-lg text-gray-700 break-words line-clamp-2" dir="auto" title={blend.name}>{blend.name}</div>
                  <div className="text-sm text-gray-500">{formatTime(blend.required_roast_time_seconds)}</div>
                  {blend.recipe && <div className="text-xs font-semibold text-amber-700">⚖ מתכון 90 ק״ג</div>}
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => { setEditingBlend(blend); setShowBlendForm(true); }}
                    className="text-amber-600 hover:text-amber-800 text-lg font-bold p-2"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => handleDeleteBlend(blend.id)}
                    className="text-red-500 hover:text-red-700 text-lg font-bold p-2"
                  >
                    🗑
                  </button>
                </div>
              </div>
            )}
          </SortableBlendGrid>
        </div>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={() => { isBusyRef.current = true; }}
        onDragEnd={handleDragEnd}
        onDragCancel={() => { isBusyRef.current = pendingReorderCountRef.current > 0; }}
      >
      <div className={`grid gap-3 2xl:gap-6 flex-1 min-h-0 ${
        isPlanFocused
          ? 'grid-cols-1 lg:grid-cols-[minmax(220px,1fr)_minmax(420px,2fr)]'
          : showOven2
            ? 'grid-cols-1 lg:grid-cols-2 xl:grid-cols-[minmax(280px,1.35fr)_repeat(2,minmax(205px,1fr))_minmax(215px,1fr)]'
            : 'grid-cols-1 lg:grid-cols-2 xl:grid-cols-[minmax(280px,1.35fr)_minmax(205px,1fr)_minmax(215px,1fr)]'
      }`}>
        {/* ── Blend Selection Grid ── */}
        {isPlanFocused && (
          <div className="bg-white rounded-2xl shadow-lg p-4 2xl:p-6 flex flex-col overflow-hidden">
            <h2 className="text-base 2xl:text-lg font-bold text-gray-700 mb-1 shrink-0">בחר תערובת להוספה</h2>
            <p className="text-gray-400 text-xs mb-2 shrink-0">
              {blends.length > 100 ? 'לחץ או גרור תערובת אל התכנית. שינוי סדר מוגבל ל-100 תערובות.' : 'לחץ או גרור תערובת אל התכנית; גרור את הידית לשינוי הסדר'}
            </p>
            <SortableBlendGrid
              blends={blends}
              className="grid grid-cols-[repeat(auto-fit,minmax(7rem,1fr))] gap-2 2xl:gap-3 overflow-y-auto flex-1 min-h-0 content-start"
              onDragStart={handleBlendDragStart}
              onDragEnd={handleBlendDragEnd}
              onDragCancel={handleBlendDragCancel}
            >
              {blend => (
                <BlendCard
                  blend={blend}
                  onClick={() => setSelectedBlend(blend)}
                />
              )}
            </SortableBlendGrid>
            {blends.length === 0 && (
              <p className="text-gray-400 text-lg text-center py-8">אין תערובות. הוסף דרך ניהול תערובות.</p>
            )}
          </div>
        )}

        {/* ── Daily Plan (HTML5 drop zone + dnd-kit sortable reorder) ── */}
        <div
          className={`bg-white rounded-2xl shadow-lg p-4 2xl:p-6 transition-all flex flex-col overflow-hidden ${
            isDragOver ? 'ring-4 ring-amber-400 bg-amber-50' : ''
          }`}
          onDragOver={handlePlanDragOver}
          onDragLeave={handlePlanDragLeave}
          onDrop={handlePlanDrop}
        >
          <div className="flex flex-col min-[1700px]:flex-row min-[1700px]:items-center justify-between gap-1.5 mb-2 shrink-0">
            <h2 className="text-base 2xl:text-lg font-bold text-gray-700">תכנית יומית</h2>
            <div className="flex w-full min-[1700px]:w-auto items-center gap-2">
              <button
                type="button"
                onClick={() => setIsPlanFocused(focused => {
                  if (!focused) setCollapsedFocusedPlanBatchIds(new Set());
                  return !focused;
                })}
                aria-pressed={isPlanFocused}
                className="shrink-0 rounded-lg bg-amber-100 px-3 py-1.5 text-sm font-bold text-amber-800 hover:bg-amber-200"
              >
                {isPlanFocused ? '↙ חזרה לתצוגה מלאה' : '↗ הרחב תכנית'}
              </button>
              <input
                type="date"
                value={planDate}
                onChange={(e) => setPlanDate(e.target.value)}
                className="min-w-0 flex-1 min-[1700px]:w-auto px-2 py-1.5 border-2 border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
              />
            </div>
          </div>

          {isDragOver && (
            <div className="text-center py-3 mb-3 bg-amber-100 rounded-xl border-2 border-dashed border-amber-400 shrink-0">
              <p className="text-amber-700 text-xl font-bold">⬇ שחרר כאן להוספה לתכנית</p>
            </div>
          )}

          <div className="space-y-1.5 overflow-y-auto flex-1 min-h-0">
            {/* ── Planned batches (shared queue, sortable) ── */}
            <SortableContext items={plannedBatches.map((b) => b.id)} strategy={verticalListSortingStrategy}>
              {plannedBatches.map((batch, index) => (
                <SortableBatchItem
                  key={batch.id}
                  batch={batch}
                  currentPlanDate={planDate}
                  isExpanded={isPlanFocused
                    ? !collapsedFocusedPlanBatchIds.has(batch.id)
                    : expandedPlannedBatchId === batch.id}
                  spacious={isPlanFocused}
                  onToggle={() => {
                    if (isPlanFocused) {
                      setCollapsedFocusedPlanBatchIds(current => {
                        const next = new Set(current);
                        if (next.has(batch.id)) next.delete(batch.id);
                        else next.add(batch.id);
                        return next;
                      });
                    } else {
                      setExpandedPlannedBatchId((current) => current === batch.id ? null : batch.id);
                    }
                  }}
                  onDelete={handleDeleteBatch}
                  onAssign={handleAssignToOven}
                  onPackage={batch => setPackageBatches([batch])}
                  onLoaderNameChange={handleUpdateLoaderName}
                  onRoastTimeChange={handleUpdateRoastTime}
                  onMove={handleMovePlannedBatch}
                  canMoveUp={index > 0}
                  canMoveDown={index < plannedBatches.length - 1}
                  ovenQueues={ovenQueues}
                  canStart={ovenQueuePosition(ovenQueues, batch) === 1
                    && ![...batches, ...strayOccupants].some(item => item.machine_id === batch.machine_id && item.status === 'in-progress')}
                  onStart={handleStart}
                  onUnassign={handleUnassign}
                />
              ))}
            </SortableContext>

            {plannedBatches.length === 0 && !isDragOver && (
              <p className="text-gray-400 text-lg text-center py-12">
                גרור תערובת לכאן או גרור אצווה אל תנור
              </p>
            )}
          </div>

          {plannedBatches.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200 text-lg text-gray-600 shrink-0">
              סה״כ: {plannedBatches.length} batchs מתוכננות
            </div>
          )}
        </div>

        {/* ── Oven panels (one per oven: in-progress + completed; drop a planned batch to start it) ── */}
        {!isPlanFocused && OVEN_IDS.filter(mid => mid !== '2' || showOven2).map((mid) => (
          <OvenPanel
            key={mid}
            mid={mid}
            ovenQueues={ovenQueues}
            items={ovenBatches(mid)}
            hasExternalActive={strayOccupants.some(
              batch => batch.machine_id === mid && batch.status === 'in-progress',
            )}
            currentPlanDate={planDate}
            expandedBatchId={expandedOvenBatchId}
            onToggleBatch={(id) => setExpandedOvenBatchId((current) => current === id ? null : id)}
            onStart={handleStart}
            onUnassign={handleUnassign}
            onStartTimer={handleStartTimer}
            onFinish={handleFinish}
            onCancel={handleCancelBatch}
            onBatchUpdated={(updated) => { setBatches(prev => prev.map(b => b.id === updated.id ? updated : b)); loadPackagingJobs(); }}
            onPackage={batch => setPackageBatches([batch])}
            onLoaderNameChange={handleUpdateLoaderName}
            onRoastTimeChange={handleUpdateRoastTime}
          />
        ))}
        {!isPlanFocused && <PackagingPanel
          jobs={packagingJobs}
          currentPlanDate={planDate}
          onAdd={() => { setEditingPackagingJob(null); setShowPackagingJobForm(true); }}
          onEdit={(job) => { setEditingPackagingJob(job); setShowPackagingJobForm(true); }}
          onDone={handlePackagingDone}
          onReopen={handlePackagingReopen}
          onInstructions={handlePackagingInstructions}
          onDelete={handlePackagingDelete}
          onReorder={handlePackagingReorder}
        />}
      </div>
      </DndContext>

      {loadingConfirmation && (
        <LoadingConfirmationModal
          batch={loadingConfirmation.batch}
          mid={loadingConfirmation.mid}
          onLoaded={(workerName) => confirmLoadedIntoOven(
            loadingConfirmation.batch.id,
            loadingConfirmation.mid,
            workerName,
          )}
          onCancel={() => setLoadingConfirmation(null)}
        />
      )}

      {showPackageManagement && <PackageManagementModal onClose={() => setShowPackageManagement(false)} />}
      {packageBatches && packageBatches.length > 0 && (
        <PackageAssignmentModal initialBatches={packageBatches} onClose={() => { setPackageBatches(null); reloadPlan(); loadPackagingJobs(); }} onBatchUpdated={handlePackageBatchUpdated} />
      )}
      {showPackagingJobForm && <PackagingJobModal planDate={planDate} blends={blends} job={editingPackagingJob} onClose={() => { setShowPackagingJobForm(false); setEditingPackagingJob(null); }} onSaved={() => { loadPackagingJobs(); reloadPlan(); }} />}

      {/* ── Add to Plan Modal ── */}
      {selectedBlend && (
        <AddToPlanModal
          blend={selectedBlend}
          onClose={() => setSelectedBlend(null)}
          onAdd={handleAddBatch}
        />
      )}

      {/* ── Blend Form Modal ── */}
      {showBlendForm && (
        <BlendModal
          blend={editingBlend}
          onClose={() => { setShowBlendForm(false); setEditingBlend(null); }}
          onSave={handleSaveBlend}
        />
      )}
    </div>
  );
}

export default Manager;
